import { Link, useLoaderData, useParams, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  Briefcase,
  CircleHelp,
  Clock,
  GraduationCap,
  Package,
  ShoppingCart,
  Target,
  User,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@/auth/client";
import {
  FY_OPTIONS,
  formatCurrency,
  formatMonthLabel,
  formatNumber,
  formatPercent,
  getCurrentFiscalQuarter,
  getFiscalYearDates,
  type Pipeline,
  resolvePipelineIds,
} from "@/components/deal-metrics/shared";
import { CurrencyInput } from "@/components/shared/currency-input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatFY, formatStartDate } from "@/lib/fiscal";
import { GOAL_BAR, GOAL_TEXT, GOAL_TEXT_YTD, goalStatus } from "@/lib/goal-status";
import { isGuest } from "@/lib/team-access";
import { cn } from "@/lib/utils";
import {
  AffiliatePerformanceSection,
  type DRAffiliatePerformanceData,
  DRAffiliatePerformanceSection,
} from "./affiliate-performance-section";
import { ChannelNumbersCard } from "./channel-numbers-card";
import { DealDrillDownSheet } from "./deal-drill-down-sheet";
import { DrillableValue, type DrillDownRequest } from "./drillable-value";
import { PerformanceReviewsTab } from "./performance-reviews-tab";
import { PtoTab } from "./pto-tab";
import { SalaryBenefitsTab } from "./salary-benefits-tab";
import {
  type ShopifyDrillDownRequest,
  ShopifyOrderDrillDownSheet,
} from "./shopify-order-drill-down-sheet";
import {
  SocialFollowersCard,
  type SocialFollowersResponse,
  useSocialBonuses,
} from "./social-followers-card";

// ── Types ────────────────────────────────────────────────────────────

type DashboardTab = "performance" | "quarterly" | "pto" | "salary" | "reviews";

interface OwnerData {
  id: string;
  name: string;
  email: string;
  image: string | null;
  role: string | null;
  reportsTo: string | null;
  directReports: { id: string; name: string; hubspotOwnerId: string | null }[];
  quickLinks?: { id: string; name: string; hubspotOwnerId: string | null }[];
  baseSalary: number | null;
  annualBonusPercent: number | null;
  hubspotOwnerId: string;
  commissionType?: string;
  nonRelationalGpmThreshold: number | null;
  relationalGpmThreshold: number | null;
  standardMarginGpmThreshold: number | null;
  hideGpm?: boolean;
  executiveView?: boolean;
  hireDate: string | null;
  workingGeniusGeniuses: string | null;
  workingGeniusCompetencies: string | null;
  workingGeniusFrustrations: string | null;
}

interface AnnualGoals {
  opportunities: number;
  closedDeals: number;
  errorCount: number;
  errorRate: number;
  grossSales: number;
  netSales: number;
  closeRate: number;
  grossProfitMargin: number;
  avgOrderValue: number;
}

interface MonthlyRow {
  month: string;
  opportunities: number;
  closedDeals: number;
  errorCount: number;
  errorRate: number;
  grossSales: number;
  netSales: number;
  grossProfitMargin: number;
  closeRate: number;
  avgOrderValue: number;
  /** Present only on Chase's and Zach's dashboards: this owner's own Relational
   *  leads/closed deals/gross sales/net sales/GPM. Non-Relational is derived as
   *  (total − relational); Non-Relational GPM combines via net-sales-weighted dollars. */
  relationalOpportunities?: number;
  relationalClosedDeals?: number;
  relationalGrossSales?: number;
  relationalNetSales?: number;
  relationalGrossProfitMargin?: number;
}

interface QuarterCommission {
  label: string;
  closedDeals: number;
  dealsMinusBaseline: number;
  errorsMade: number;
  payOut: number;
}

interface NRQuarterCommission {
  label: string;
  totalOrders: number;
  totalErrors: number;
  totalGrossProfit: number;
  averageGpm: number;
  eligible: number;
  payOut: number;
}

interface BlendedQuarterCommission {
  label: string;
  totalClosed: number;
  errorsMade: number;
  eligible: number;
  relClosed: number;
  nonRelClosed: number;
  relEligible: number;
  nonRelEligible: number;
  relAvgGp: number;
  nonRelAvgGp: number;
  relRatePercent: number;
  nonRelRatePercent: number;
  relPayOut: number;
  nonRelPayOut: number;
  payOut: number;
}

interface CommissionConfig {
  quarterlyBaseline: number;
  perDeal: number;
  errorRateThreshold: number;
  nonRelationalGpmThreshold?: number | null;
  relationalGpmThreshold?: number | null;
}

interface SourceGoalSetData {
  id: string;
  name: string;
  sources: string[];
  targets: Record<string, number>;
  actuals: AnnualGoals;
  monthlyTracker: MonthlyRow[];
}

interface CreatedOrderActuals {
  popupUnits: number;
  webUnits: number;
  totalUnits: number;
  grossSales: number;
  popupGrossSales: number;
}

interface CreatedOrderMonthlyRow {
  month: string;
  popupUnits: number;
  webUnits: number;
  totalUnits: number;
  grossSales: number;
  popupGrossSales: number;
}

interface CreatedGoalsData {
  targets: Record<string, number>;
  actuals: CreatedOrderActuals;
  monthlyTracker: CreatedOrderMonthlyRow[];
}

interface FPReportingActuals {
  leadGeneration: number;
  digitalLeadGeneration: number;
  digitalCac: null;
  digitalCloseRate: number;
  digitalGrossSales: number;
}

interface FPReportingMonthlyRow {
  month: string;
  leadGeneration: number;
  digitalLeadGeneration: number;
  digitalCac: null;
  digitalCloseRate: number;
  digitalGrossSales: number;
}

interface FPDigitalSourceBreakdown {
  actuals: {
    leadGeneration: number;
    digitalLeadGeneration: number;
    digitalCloseRate: number;
    digitalGrossSales: number;
  };
  monthlyTracker: {
    month: string;
    leadGeneration: number;
    digitalLeadGeneration: number;
    digitalCloseRate: number;
    digitalGrossSales: number;
  }[];
}

const FP_DIGITAL_KEY_MAP: Record<string, keyof FPDigitalSourceBreakdown["actuals"]> = {
  lead_generation: "leadGeneration",
  digital_lead_generation: "digitalLeadGeneration",
  digital_close_rate: "digitalCloseRate",
  digital_gross_sales: "digitalGrossSales",
};

interface FPReportingGoalsData {
  targets: Record<string, number>;
  actuals: FPReportingActuals;
  monthlyTracker: FPReportingMonthlyRow[];
  digitalSources: string[];
  digitalSourceBreakdowns?: Record<string, FPDigitalSourceBreakdown>;
}

interface ExecutiveGoalsData {
  actuals: AnnualGoals;
  monthlyTracker: MonthlyRow[];
  channels: string[];
  channelBreakdowns?: Record<
    string,
    {
      actuals: AnnualGoals;
      monthlyTracker: MonthlyRow[];
      sources: string[];
    }
  >;
}

interface OwnerMetricsResponse {
  owner: OwnerData;
  annualGoals: AnnualGoals;
  annualGoalTargets: Record<string, number>;
  monthlyTracker: MonthlyRow[];
  commissionConfig: CommissionConfig;
  quarters: QuarterCommission[];
  nrQuarters: NRQuarterCommission[] | null;
  smQuarters: NRQuarterCommission[] | null;
  blendedQuarters: BlendedQuarterCommission[] | null;
  /** Quarters on new mid-year (margin-based) terms; null when the owner has none. */
  commissionTermChange: {
    quarterlyBaseline: number;
    gpmPercent: number;
    quarters: NRQuarterCommission[];
  } | null;
  orderSummary: {
    fpOrders: number | null;
    createdOrders: number | null;
    totalOrders: number | null;
  };
  bonusActivation: { currentRevenue: number; revenueGoal: number | null };
  createdGoals?: CreatedGoalsData | null;
  fpReportingGoals?: FPReportingGoalsData | null;
  campusKpis: {
    campuses: { campus: string; year: number | null; startDate: string | null }[];
    monthlyData: { campus: string; month: string; leads: number; deals: number }[];
    commissionRates: { year2: number | null; year3: number | null };
  } | null;
  campusBudget: {
    annualBudget: number;
    monthlySpend: Record<string, number>;
  } | null;
  byxGoals?: { actuals: AnnualGoals; monthlyTracker: MonthlyRow[] } | null;
  sourceGoalSets?: SourceGoalSetData[];
  nonRelationalGoals?: {
    targets: Record<string, number>;
    actuals: AnnualGoals;
    monthlyTracker: MonthlyRow[];
    sources: string[];
    nrChannelBreakdowns?: Record<
      string,
      {
        actuals: AnnualGoals;
        monthlyTracker: MonthlyRow[];
        targets: Record<string, number>;
      }
    >;
  } | null;
  affiliatePerformance?: {
    affiliateBonusPerDeal: number;
    rows: {
      affiliateName: string;
      closedDeals: number;
      bonusPerDeal: number;
      totalEarned: number;
      quarters: { label: string; closedDeals: number; earned: number }[];
    }[];
  } | null;
  affiliateGoals?: {
    targets: Record<string, number>;
    actuals: AnnualGoals;
    monthlyTracker: MonthlyRow[];
    exclusions: string[];
  } | null;
  drAffiliatePerformance?: DRAffiliatePerformanceData | null;
  executiveGoals?: ExecutiveGoalsData | null;
}

// ── Component ────────────────────────────────────────────────────────

const { fy: currentFY } = getCurrentFiscalQuarter();
const { start: DEFAULT_START, end: DEFAULT_END } = getFiscalYearDates(currentFY, "FY");

interface OwnerMetricDashboardProps {
  pipelines: Pipeline[];
  ownerId: string;
  referrer?: string;
  ownerDetailId?: string;
  hideBackButton?: boolean;
}

/**
 * Wrapper for the admin route — reads pipelines/ownerId/search from route context.
 */
export function OwnerMetricDashboardRoute() {
  const { pipelines } = useLoaderData({
    from: "/_authenticated/owner-metrics/$ownerId",
  }) as { pipelines: Pipeline[] };
  const { ownerId } = useParams({ from: "/_authenticated/owner-metrics/$ownerId" });

  // biome-ignore lint/suspicious/noExplicitAny: route type not yet in TanStack Router's generated types
  const { from: referrer, ownerDetailId } = useSearch({
    from: "/_authenticated/owner-metrics/$ownerId" as any,
  }) as { from?: string; ownerDetailId?: string };

  return (
    <OwnerMetricDashboard
      pipelines={pipelines}
      ownerId={ownerId}
      referrer={referrer}
      ownerDetailId={ownerDetailId}
    />
  );
}

export function OwnerMetricDashboard(props: OwnerMetricDashboardProps) {
  const { pipelines, ownerId, referrer, ownerDetailId, hideBackButton } = props;

  const { data: session } = useSession();
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === "admin";
  // Guests (contractors) only get the Performance view — no HR tabs.
  const guestViewer = isGuest(session?.user?.email);

  const [selectedFY, setSelectedFY] = useState(currentFY);
  const [activeTab, setActiveTab] = useState<DashboardTab>("performance");

  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);
  const [data, setData] = useState<OwnerMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  function applyPeriod(fy: number) {
    setSelectedFY(fy);
    const { start, end } = getFiscalYearDates(fy, "FY");
    setStartDate(start);
    setEndDate(end);
  }

  const { salesPipelineId, prodPipelineId, excludeOppStageIds, excludeClosedStageIds } = useMemo(
    () => resolvePipelineIds(pipelines),
    [pipelines],
  );

  const fetchData = useCallback(async () => {
    if (!prodPipelineId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate, prodPipelineId });
      if (salesPipelineId) params.set("salesPipelineId", salesPipelineId);
      if (excludeOppStageIds.length > 0)
        params.set("excludeOppStageIds", excludeOppStageIds.join(","));
      if (excludeClosedStageIds.length > 0)
        params.set("excludeClosedStageIds", excludeClosedStageIds.join(","));

      const res = await fetch(`/api/owner-metrics/${ownerId}?${params}`);
      if (res.ok) {
        setData(await res.json());
      }
    } finally {
      setLoading(false);
    }
  }, [
    ownerId,
    startDate,
    endDate,
    salesPipelineId,
    prodPipelineId,
    excludeOppStageIds,
    excludeClosedStageIds,
  ]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // Drill-down state
  const [drillDown, setDrillDown] = useState<DrillDownRequest | null>(null);
  const [shopifyDrillDown, setShopifyDrillDown] = useState<ShopifyDrillDownRequest | null>(null);

  // Quarterly Commission tab only shows for people with an actual commission/bonus plan configured
  // Social media bonuses (e.g. Hope's follower + lead bonuses) live on the
  // Quarterly Commissions tab too; null for everyone without them.
  const socialBonuses = useSocialBonuses(ownerId);

  const hasQuarterlyCommissionData = useMemo(() => {
    if (!data) return false;
    const hasCommissionGoals = Object.keys(data.annualGoalTargets).some(
      (k) =>
        !k.startsWith("campus_") &&
        !k.startsWith("nr_") &&
        !k.startsWith("aff_") &&
        !k.startsWith("created_") &&
        !k.startsWith("fp_"),
    );
    return (
      hasCommissionGoals ||
      !!data.affiliatePerformance ||
      !!data.drAffiliatePerformance ||
      !!data.commissionTermChange
    );
  }, [data]);

  const baseParams = useMemo(() => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      prodPipelineId: prodPipelineId ?? "",
    });
    if (salesPipelineId) params.set("salesPipelineId", salesPipelineId);
    if (excludeOppStageIds.length > 0)
      params.set("excludeOppStageIds", excludeOppStageIds.join(","));
    if (excludeClosedStageIds.length > 0)
      params.set("excludeClosedStageIds", excludeClosedStageIds.join(","));
    return params;
  }, [
    startDate,
    endDate,
    prodPipelineId,
    salesPipelineId,
    excludeOppStageIds,
    excludeClosedStageIds,
  ]);

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Top Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          {!hideBackButton && (
            <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
              {referrer === "org-chart" ? (
                <Link to="/org-chart">
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Org Chart
                </Link>
              ) : referrer === "owner-detail" && ownerDetailId ? (
                <Link to="/owner-detail/$ownerId" params={{ ownerId: ownerDetailId }}>
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Owner Profile
                </Link>
              ) : (
                <Link to="/deal-metrics">
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Deal Metrics
                </Link>
              )}
            </Button>
          )}
          {data && (
            <h1 className="font-heading text-2xl font-bold tracking-tight">{data.owner.name}</h1>
          )}
          {loading && !data && <Skeleton className="h-8 w-48" />}
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(selectedFY)} onValueChange={(v) => applyPeriod(Number(v))}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FY_OPTIONS.map((fy) => (
                <SelectItem key={fy} value={String(fy)}>
                  {formatFY(fy)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && !data ? (
        <Skeleton className="h-40 w-full max-w-md" />
      ) : data ? (
        <ProfileCard
          owner={data.owner}
          canEdit={isAdmin || session?.user?.email === data.owner.email}
        />
      ) : null}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DashboardTab)}>
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          {(hasQuarterlyCommissionData || socialBonuses) && (
            <TabsTrigger value="quarterly">Quarterly Commissions</TabsTrigger>
          )}
          {!guestViewer && (
            <>
              <TabsTrigger value="pto">PTO</TabsTrigger>
              <TabsTrigger value="salary">Salary/Benefits</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
            </>
          )}
        </TabsList>
      </Tabs>

      {activeTab === "pto" && <PtoTab ownerId={ownerId} />}
      {activeTab === "salary" && <SalaryBenefitsTab ownerId={ownerId} />}
      {activeTab === "reviews" && <PerformanceReviewsTab ownerId={ownerId} />}

      {(activeTab === "performance" || activeTab === "quarterly") &&
        (loading && !data ? (
          <LoadingSkeleton />
        ) : data ? (
          <DashboardContent
            data={data}
            fiscalYear={selectedFY}
            activeTab={activeTab}
            socialBonuses={socialBonuses}
            onDrillDown={setDrillDown}
            onShopifyDrillDown={setShopifyDrillDown}
          />
        ) : (
          <Card className="py-12">
            <CardContent className="text-center text-muted-foreground">
              No data available for this owner.
            </CardContent>
          </Card>
        ))}

      <DealDrillDownSheet
        drillDown={drillDown}
        onClose={() => setDrillDown(null)}
        ownerId={ownerId}
        baseParams={baseParams}
        pipelines={pipelines}
      />

      <ShopifyOrderDrillDownSheet
        drillDown={shopifyDrillDown}
        onClose={() => setShopifyDrillDown(null)}
        ownerId={ownerId}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
}

// ── Loading Skeleton ─────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        {/* biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders have no stable ID */}
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={`sk-${i}`} className="py-5">
            <CardContent className="flex flex-col gap-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Skeleton className="h-64" />
        <Skeleton className="h-64 lg:col-span-2" />
      </div>
    </div>
  );
}

// ── Dashboard Content ────────────────────────────────────────────────

function DashboardContent({
  data,
  fiscalYear,
  activeTab,
  socialBonuses,
  onDrillDown,
  onShopifyDrillDown,
}: {
  data: OwnerMetricsResponse;
  fiscalYear: number;
  activeTab: "performance" | "quarterly";
  socialBonuses: SocialFollowersResponse | null;
  onDrillDown: (req: DrillDownRequest) => void;
  onShopifyDrillDown: (req: ShopifyDrillDownRequest) => void;
}) {
  const {
    owner,
    annualGoals,
    annualGoalTargets,
    monthlyTracker,
    commissionConfig,
    quarters,
    nrQuarters,
    smQuarters,
    blendedQuarters,
    commissionTermChange,
    orderSummary,
    bonusActivation,
    createdGoals,
    fpReportingGoals,
    campusKpis,
    campusBudget,
    byxGoals,
    sourceGoalSets,
    nonRelationalGoals,
    affiliateGoals,
    affiliatePerformance,
    drAffiliatePerformance,
    executiveGoals,
  } = data;

  // Same gate used both here (goals/tracker) and in the Quarterly Review tab
  // (commission cards) — both are meaningless without a configured metric goal.
  const hasCommissionGoals = Object.keys(annualGoalTargets).some(
    (k) =>
      !k.startsWith("campus_") &&
      !k.startsWith("nr_") &&
      !k.startsWith("aff_") &&
      !k.startsWith("created_") &&
      !k.startsWith("fp_"),
  );

  // Executive-view owners (e.g. FP Revenue Lead) carry company-wide goals, so
  // the Annual Goals card + tracker must compare them against company-wide
  // actuals — not this person's individual deals, which would show every bar
  // near 0% and a misleading close rate.
  const goalActuals = executiveGoals ? executiveGoals.actuals : annualGoals;
  const goalTracker = executiveGoals ? executiveGoals.monthlyTracker : monthlyTracker;

  // Quarters on new mid-year terms render as margin-based cards in place of the
  // owner's usual card; earlier quarters keep their original math.
  const newTermsCard = (label: string) => {
    const q = commissionTermChange?.quarters.find((x) => x.label === label);
    if (!q || !commissionTermChange) return null;
    return (
      <SMQuarterCard
        key={label}
        quarter={q}
        baseline={commissionTermChange.quarterlyBaseline}
        smGpmThreshold={commissionTermChange.gpmPercent}
        note={`New terms: ${commissionTermChange.quarterlyBaseline} deals · ${commissionTermChange.gpmPercent}% of GP`}
        onDrillDown={(metric, dlabel) =>
          onDrillDown({ metric, section: "commission", quarter: label, label: dlabel })
        }
      />
    );
  };

  // Someone whose only commission is new mid-year terms (no earlier plan on the
  // Owners page) gets their own layout: earlier quarters read "not eligible".
  const hasBasePlan =
    owner.commissionType !== "standard" ||
    commissionConfig.quarterlyBaseline > 0 ||
    commissionConfig.perDeal > 0 ||
    commissionConfig.errorRateThreshold > 0;
  const termsOnlyLayout = commissionTermChange && !hasBasePlan && (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <SMCommissionConfigCard
        config={{
          quarterlyBaseline: commissionTermChange.quarterlyBaseline,
          standardMarginGpmThreshold: commissionTermChange.gpmPercent,
          errorRateThreshold: commissionConfig.errorRateThreshold,
        }}
      />
      {(["Q1", "Q2", "Q3", "Q4"] as const).map(
        (label) =>
          newTermsCard(label) ?? (
            <Card key={label} className="opacity-60">
              <CardHeader className="pb-2">
                <CardTitle className="text-center font-heading text-xl tracking-tight">
                  {label}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center text-sm text-muted-foreground">
                Not commission-eligible this quarter
              </CardContent>
            </Card>
          ),
      )}
    </div>
  );

  if (activeTab === "quarterly") {
    return (
      <div className="space-y-6 animate-fade-up">
        {termsOnlyLayout ||
          (hasCommissionGoals &&
            (owner.commissionType === "blended" && blendedQuarters ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <NRCommissionConfigCard
                  config={{
                    quarterlyBaseline: commissionConfig.quarterlyBaseline,
                    nonRelationalGpmThreshold: owner.nonRelationalGpmThreshold,
                    relationalGpmThreshold: owner.relationalGpmThreshold,
                    errorRateThreshold: commissionConfig.errorRateThreshold,
                  }}
                />
                {blendedQuarters.map(
                  (q) =>
                    newTermsCard(q.label) ?? (
                      <BlendedQuarterCard
                        key={q.label}
                        quarter={q}
                        baseline={commissionConfig.quarterlyBaseline}
                        onDrillDown={(metric, label) =>
                          onDrillDown({ metric, section: "commission", quarter: q.label, label })
                        }
                      />
                    ),
                )}
              </div>
            ) : owner.commissionType === "non_relational" && nrQuarters ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <NRCommissionConfigCard
                  config={{
                    quarterlyBaseline: commissionConfig.quarterlyBaseline,
                    nonRelationalGpmThreshold: owner.nonRelationalGpmThreshold,
                    relationalGpmThreshold: owner.relationalGpmThreshold,
                    errorRateThreshold: commissionConfig.errorRateThreshold,
                  }}
                />
                {nrQuarters.map(
                  (q) =>
                    newTermsCard(q.label) ?? (
                      <NRQuarterCard
                        key={q.label}
                        quarter={q}
                        baseline={commissionConfig.quarterlyBaseline}
                        nrGpmThreshold={owner.nonRelationalGpmThreshold ?? 0}
                        onDrillDown={(metric, label) =>
                          onDrillDown({ metric, section: "nr", quarter: q.label, label })
                        }
                      />
                    ),
                )}
              </div>
            ) : owner.commissionType === "standard_margin" && smQuarters ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <SMCommissionConfigCard
                  config={{
                    quarterlyBaseline: commissionConfig.quarterlyBaseline,
                    standardMarginGpmThreshold: owner.standardMarginGpmThreshold,
                    errorRateThreshold: commissionConfig.errorRateThreshold,
                  }}
                />
                {smQuarters.map(
                  (q) =>
                    newTermsCard(q.label) ?? (
                      <SMQuarterCard
                        key={q.label}
                        quarter={q}
                        baseline={commissionConfig.quarterlyBaseline}
                        smGpmThreshold={owner.standardMarginGpmThreshold ?? 0}
                        onDrillDown={(metric, label) =>
                          onDrillDown({ metric, section: "commission", quarter: q.label, label })
                        }
                      />
                    ),
                )}
              </div>
            ) : (
              (commissionConfig.quarterlyBaseline > 0 ||
                commissionConfig.perDeal > 0 ||
                commissionConfig.errorRateThreshold > 0) && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  <CommissionConfigCard
                    config={{
                      ...commissionConfig,
                      nonRelationalGpmThreshold: owner.nonRelationalGpmThreshold,
                      relationalGpmThreshold: owner.relationalGpmThreshold,
                    }}
                  />
                  {quarters.map(
                    (q) =>
                      newTermsCard(q.label) ?? (
                        <QuarterCard
                          key={q.label}
                          quarter={q}
                          config={commissionConfig}
                          onDrillDown={(metric, label) =>
                            onDrillDown({ metric, section: "commission", quarter: q.label, label })
                          }
                        />
                      ),
                  )}
                </div>
              )
            )))}

        {/* Affiliate Coaching — shown when bonus config or mappings exist */}
        {affiliatePerformance && <AffiliatePerformanceSection data={affiliatePerformance} />}

        {/* DR Affiliate Coaching — shown when DR bonus config exists and owner has direct reports */}
        {drAffiliatePerformance && (
          <DRAffiliatePerformanceSection data={drAffiliatePerformance} onDrillDown={onDrillDown} />
        )}

        {/* Social Media Bonuses — only for people whose pay tracks social growth */}
        {socialBonuses && <SocialFollowersCard data={socialBonuses} />}

        {!hasCommissionGoals &&
          !termsOnlyLayout &&
          !affiliatePerformance &&
          !drAffiliatePerformance &&
          !socialBonuses && (
            <Card className="py-12">
              <CardContent className="text-center text-muted-foreground">
                No commission or quarterly bonus data configured for this person.
              </CardContent>
            </Card>
          )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Row 1: Order Stubs + Bonus Activation */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <OrderMetricCard
          label="FP Orders"
          value={orderSummary.fpOrders}
          icon={<Package className="h-3.5 w-3.5" />}
        />
        <OrderMetricCard
          label="Created Orders"
          value={orderSummary.createdOrders}
          icon={<ShoppingCart className="h-3.5 w-3.5" />}
        />
        <OrderMetricCard
          label="Total Orders"
          value={orderSummary.totalOrders}
          icon={<Briefcase className="h-3.5 w-3.5" />}
        />
      </div>
      <BonusActivationCard
        currentRevenue={bonusActivation.currentRevenue}
        revenueGoal={bonusActivation.revenueGoal}
        fiscalYear={fiscalYear}
      />

      {/* Executive Dashboard — company-wide monthly performance, only when executiveView flag is on */}
      {executiveGoals && (
        <ExecutiveDashboardSection data={executiveGoals} onDrillDown={onDrillDown} />
      )}

      {/* Created Goals — only shown when created targets are configured */}
      {createdGoals && (
        <CreatedReportingSection
          data={createdGoals}
          monthlyTracker={monthlyTracker}
          onDrillDown={onShopifyDrillDown}
        />
      )}

      {/* FP Reporting Goals — only shown when fp_ targets are configured */}
      {fpReportingGoals && (
        <FPReportingSection
          data={fpReportingGoals}
          monthlyTracker={monthlyTracker}
          onDrillDown={onDrillDown}
        />
      )}

      {/* Channel Numbers — company-wide lead-channel results for people tied to a channel */}
      <ChannelNumbersCard ownerId={owner.id} fiscalYear={fiscalYear} />

      {/* Campus KPIs — only shown when campuses are configured */}
      {campusKpis && (
        <CampusKPIsSection
          data={campusKpis}
          monthlyTracker={monthlyTracker}
          campusTargets={{
            leads: annualGoalTargets.campus_er_leads ?? null,
            dealsWon: annualGoalTargets.campus_er_deals_won ?? null,
          }}
          onDrillDown={onDrillDown}
        />
      )}

      {/* BYX — only sent for the owners who oversee BYX */}
      {byxGoals && (
        <ByxSection data={byxGoals} monthlyTracker={monthlyTracker} onDrillDown={onDrillDown} />
      )}

      {/* Non-Relational Goals — only shown when nr targets are configured */}
      {nonRelationalGoals && (
        <NonRelationalGoalsSection
          data={nonRelationalGoals}
          monthlyTracker={monthlyTracker}
          onDrillDown={onDrillDown}
          hideGpm={owner.hideGpm}
        />
      )}

      {/* Affiliate Goals — only shown when aff targets are configured */}
      {affiliateGoals && (
        <AffiliateGoalsDashboardSection
          data={affiliateGoals}
          monthlyTracker={monthlyTracker}
          onDrillDown={onDrillDown}
          hideGpm={owner.hideGpm}
        />
      )}

      {/* Source Goal Sets — only shown when sets are configured */}
      {sourceGoalSets &&
        sourceGoalSets.length > 0 &&
        sourceGoalSets.map((set) => (
          <SourceGoalSetSection
            key={set.id}
            set={set}
            monthlyTracker={monthlyTracker}
            onDrillDown={onDrillDown}
            hideGpm={owner.hideGpm}
          />
        ))}

      {/* Annual Goals + Monthly Tracker — only shown when commission-related metric goals are configured */}
      {hasCommissionGoals && (
        <>
          <AnnualGoalsCard
            actuals={goalActuals}
            targets={annualGoalTargets}
            onDrillDown={(metric, label) =>
              onDrillDown({ metric, section: "main", label, companyWide: !!executiveGoals })
            }
            hideGpm={owner.hideGpm}
          />
          <MonthlyTrackerTable
            rows={goalTracker}
            totals={goalActuals}
            hideGpm={owner.hideGpm}
            targets={annualGoalTargets}
            onCellClick={(metric, label, month) => {
              onDrillDown({
                metric,
                section: "main",
                companyWide: !!executiveGoals,
                month,
                label: `${label} — ${formatMonthLabel(month)}`,
              });
            }}
          />
        </>
      )}

      {/* Campus Budget — only shown when budget is configured */}
      {campusBudget && (
        <CampusBudgetSection
          campusBudget={campusBudget}
          ownerId={owner.id}
          monthlyTracker={monthlyTracker}
        />
      )}
    </div>
  );
}

// ── Profile Card ─────────────────────────────────────────────────────

function formatSimpleDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ProfileCard({ owner, canEdit }: { owner: OwnerData; canEdit: boolean }) {
  const [editOpen, setEditOpen] = useState(false);
  const [geniuses, setGeniuses] = useState(owner.workingGeniusGeniuses ?? "");
  const [competencies, setCompetencies] = useState(owner.workingGeniusCompetencies ?? "");
  const [frustrations, setFrustrations] = useState(owner.workingGeniusFrustrations ?? "");
  const [saving, setSaving] = useState(false);

  // Local display state so the card reflects a save without a full dashboard refetch
  const [display, setDisplay] = useState({
    workingGeniusGeniuses: owner.workingGeniusGeniuses,
    workingGeniusCompetencies: owner.workingGeniusCompetencies,
    workingGeniusFrustrations: owner.workingGeniusFrustrations,
  });

  async function saveProfileExtra() {
    setSaving(true);
    try {
      const res = await fetch(`/api/owners/${owner.id}/profile-extra`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workingGeniusGeniuses: geniuses,
          workingGeniusCompetencies: competencies,
          workingGeniusFrustrations: frustrations,
        }),
      });
      if (res.ok) {
        setDisplay(await res.json());
        setEditOpen(false);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="relative h-full overflow-hidden gap-3 py-4">
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/[0.03] to-transparent pointer-events-none" />
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            Profile
          </CardTitle>
          {canEdit && (
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Profile Details</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Working Genius Profile
                    </Label>
                    <div className="space-y-1">
                      <Label className="text-xs">Geniuses</Label>
                      <Input
                        placeholder="e.g. Wonder, Invention"
                        value={geniuses}
                        onChange={(e) => setGeniuses(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Competencies</Label>
                      <Input
                        placeholder="e.g. Discernment, Galvanizing"
                        value={competencies}
                        onChange={(e) => setCompetencies(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Frustrations</Label>
                      <Input
                        placeholder="e.g. Enablement, Tenacity"
                        value={frustrations}
                        onChange={(e) => setFrustrations(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button size="sm" onClick={saveProfileExtra} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          {/* Identity */}
          <div className="flex items-start gap-4 lg:w-80 lg:shrink-0">
            <Avatar className="h-28 w-28 shrink-0 ring-2 ring-brand-gold/20">
              {owner.image && <AvatarImage src={owner.image} alt={owner.name} />}
              <AvatarFallback className="bg-brand-navy text-brand-gold text-2xl font-semibold">
                {owner.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 pt-1">
              <p className="font-heading text-lg font-semibold tracking-tight">{owner.name}</p>
              {owner.role && <p className="text-sm text-accent-foreground/80">{owner.role}</p>}
              <p className="mt-1 text-xs text-muted-foreground">
                Reports to {owner.reportsTo ?? "—"}
              </p>
            </div>
          </div>

          <div className="hidden w-px self-stretch bg-border lg:block" />

          {/* Key details */}
          <dl className="grid flex-1 grid-cols-2 gap-x-6 gap-y-3 text-xs sm:grid-cols-4 lg:pt-1">
            <div>
              <dt className="text-muted-foreground">Hire Date</dt>
              <dd className="mt-0.5 tabular-nums font-medium">
                {owner.hireDate ? formatSimpleDate(owner.hireDate) : "—"}
              </dd>
            </div>
            <div className="col-span-2 sm:col-span-3">
              <dt className="text-muted-foreground">Working Genius</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-1">
                {display.workingGeniusGeniuses ||
                display.workingGeniusCompetencies ||
                display.workingGeniusFrustrations ? (
                  <>
                    {display.workingGeniusGeniuses && (
                      <WorkingGeniusRow
                        label="Geniuses"
                        value={display.workingGeniusGeniuses}
                        colorClass="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                      />
                    )}
                    {display.workingGeniusCompetencies && (
                      <WorkingGeniusRow
                        label="Competencies"
                        value={display.workingGeniusCompetencies}
                        colorClass="bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
                      />
                    )}
                    {display.workingGeniusFrustrations && (
                      <WorkingGeniusRow
                        label="Frustrations"
                        value={display.workingGeniusFrustrations}
                        colorClass="bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                      />
                    )}
                  </>
                ) : (
                  <span className="font-normal text-muted-foreground/60">
                    {canEdit ? "Not set — click Edit to add" : "Not set"}
                  </span>
                )}
              </dd>
            </div>
            {owner.directReports.length > 0 && (
              <div className="col-span-2 sm:col-span-4">
                <dt className="text-muted-foreground">Direct Reports</dt>
                <dd className="mt-1 flex flex-wrap gap-1">
                  {owner.directReports.map((r) => (
                    <Link
                      key={r.id}
                      to="/owner-metrics/$ownerId"
                      params={{ ownerId: r.hubspotOwnerId ?? r.id }}
                      className="inline-block rounded bg-muted px-1.5 py-0.5 text-brand-navy hover:underline"
                    >
                      {r.name}
                    </Link>
                  ))}
                </dd>
              </div>
            )}
            {owner.quickLinks && owner.quickLinks.length > 0 && (
              <div className="col-span-2 sm:col-span-4">
                <dt className="text-muted-foreground">Quick Links</dt>
                <dd className="mt-1 flex flex-wrap gap-1">
                  {owner.quickLinks.map((r) => (
                    <Link
                      key={r.id}
                      to="/owner-metrics/$ownerId"
                      params={{ ownerId: r.hubspotOwnerId ?? r.id }}
                      className="inline-block rounded bg-muted px-1.5 py-0.5 text-brand-navy hover:underline"
                    >
                      {r.name}
                    </Link>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkingGeniusRow({
  label,
  value,
  colorClass,
}: {
  label: string;
  value: string;
  colorClass: string;
}) {
  return (
    <div className={cn("rounded-md px-2 py-1 text-xs whitespace-nowrap", colorClass)}>
      <span className="font-medium">{label}:</span> {value}
    </div>
  );
}

// ── Stub Metric Card ─────────────────────────────────────────────────

function StubMetricCard({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <Card className="gap-2 py-4">
      <CardHeader className="pb-0">
        <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center gap-1 text-center">
        <span className="text-2xl font-bold tabular-nums text-muted-foreground/40">—</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/50">
          Coming Soon
        </span>
      </CardContent>
    </Card>
  );
}

// ── Order Metric Card ─────────────────────────────────────────────────

function OrderMetricCard({
  label,
  value,
  icon,
  onDrillDown,
}: {
  label: string;
  value: number | null;
  icon?: React.ReactNode;
  onDrillDown?: () => void;
}) {
  if (value == null) return <StubMetricCard label={label} icon={icon} />;

  return (
    <Card className="gap-2 py-4">
      <CardHeader className="pb-0">
        <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center gap-1 text-center">
        <DrillableValue onClick={onDrillDown}>
          <span className="text-2xl font-bold tabular-nums">{formatNumber(value)}</span>
        </DrillableValue>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/50">
          Company-wide
        </span>
      </CardContent>
    </Card>
  );
}

// ── Bonus Activation Card ────────────────────────────────────────────

function BonusActivationCard({
  currentRevenue,
  revenueGoal,
  fiscalYear,
}: {
  currentRevenue: number;
  revenueGoal: number | null;
  fiscalYear: number;
}) {
  const hasGoal = revenueGoal !== null && revenueGoal > 0;
  const pct = hasGoal ? Math.min(currentRevenue / revenueGoal, 1) : 0;
  const widthPct = `${(pct * 100).toFixed(1)}%`;

  // Projection: scale current revenue by days remaining in fiscal year
  const now = new Date();
  const fyStart = new Date(fiscalYear - 1, 3, 1); // Apr 1 of prior year
  const fyEnd = new Date(fiscalYear, 2, 31); // Mar 31 of fiscal year
  const totalDays = Math.round((fyEnd.getTime() - fyStart.getTime()) / 86400000) + 1;
  const daysElapsed = Math.max(1, Math.round((now.getTime() - fyStart.getTime()) / 86400000) + 1);
  const isCurrentFY = now >= fyStart && now <= fyEnd;
  const projected = isCurrentFY ? (currentRevenue / daysElapsed) * totalDays : null;
  const projectedPct = hasGoal && projected !== null ? projected / revenueGoal : null;
  const onTrack = projectedPct !== null ? projectedPct >= 1 : null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Briefcase className="h-3.5 w-3.5" />
            Bonus Activation Tracker
          </span>
          {hasGoal ? (
            <span className="text-xs tabular-nums text-muted-foreground">
              {formatCurrency(currentRevenue)} / {formatCurrency(revenueGoal)}
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/50">
              No target set
            </span>
          )}
        </div>
        <div className="relative h-6 w-full overflow-hidden rounded-full bg-muted/60">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent/60 to-accent transition-[width] duration-500"
            style={{ width: hasGoal ? widthPct : "0%" }}
          />
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {hasGoal ? `${formatPercent(pct)} of Goal` : "FOC Total Revenue"}
          </span>
        </div>
        {projected !== null && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Projected Year-End</span>
            <span
              className={cn(
                "tabular-nums font-semibold",
                GOAL_TEXT[goalStatus(projected, revenueGoal)],
              )}
            >
              {formatCurrency(projected)}
              {hasGoal && (
                <span className="ml-1.5 font-normal text-muted-foreground">
                  ({formatPercent(Math.min(projectedPct ?? 0, 9.99))} of goal)
                </span>
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Shared Metric Helpers ─────────────────────────────────────────────

const PERCENT_METRICS = new Set(["error_rate", "close_rate", "gross_profit_margin"]);

function buildMetricsList(actuals: AnnualGoals, hideGpm = false) {
  const list = [
    {
      key: "opportunities",
      label: "Opportunities",
      actual: actuals.opportunities,
      format: formatNumber,
    },
    {
      key: "closed_deals",
      label: "Closed Deals",
      actual: actuals.closedDeals,
      format: formatNumber,
    },
    { key: "error_rate", label: "Error Rate", actual: actuals.errorRate, format: formatPercent },
    {
      key: "gross_sales",
      label: "Gross Sales",
      actual: actuals.grossSales,
      format: formatCurrency,
    },
    { key: "close_rate", label: "Close Rate", actual: actuals.closeRate, format: formatPercent },
    {
      key: "gross_profit_margin",
      label: "Gross Profit Margin",
      actual: actuals.grossProfitMargin,
      format: formatPercent,
    },
    {
      key: "avg_order_value",
      label: "Avg Order Value",
      actual: actuals.avgOrderValue,
      format: formatCurrency,
    },
  ];
  return hideGpm ? list.filter((m) => m.key !== "gross_profit_margin") : list;
}

function normalizeTargets(targets: Record<string, number>) {
  const normalized: Record<string, number> = {};
  for (const [key, value] of Object.entries(targets)) {
    normalized[key] = PERCENT_METRICS.has(key) ? value / 100 : value;
  }
  return normalized;
}

// Mapping from display metric key → drill-down metric type
const METRIC_KEY_TO_DRILL: Record<string, DrillDownRequest["metric"] | null> = {
  opportunities: "opportunities",
  closed_deals: "closedDeals",
  error_rate: null,
  gross_sales: "grossSales",
  close_rate: null,
  gross_profit_margin: "gpm",
};

/** Renders a grid of metric targets vs actuals with progress bars. */
function MetricTargetsGrid({
  actuals,
  targets,
  onDrillDown,
  hideGpm = false,
}: {
  actuals: AnnualGoals;
  targets: Record<string, number>;
  onDrillDown?: (metric: DrillDownRequest["metric"], label: string) => void;
  hideGpm?: boolean;
}) {
  const metrics = buildMetricsList(actuals, hideGpm);
  const normalizedTargets = normalizeTargets(targets);
  const hasAnyTarget = metrics.some((m) => normalizedTargets[m.key] !== undefined);

  if (!hasAnyTarget) return null;

  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {metrics.map((m) => {
        const target = normalizedTargets[m.key];
        if (target === undefined) return null;
        const actual = m.actual;
        const isLessThan = m.key === "error_rate";
        const pct = actual !== null && target > 0 ? Math.round((actual / target) * 100) : null;
        const met =
          actual !== null && target > 0
            ? isLessThan
              ? actual <= target
              : actual >= target
            : false;
        const status = goalStatus(actual, target, isLessThan ? "lte" : "gte");
        const drillMetric = METRIC_KEY_TO_DRILL[m.key];
        return (
          <div key={m.key} className="space-y-1.5">
            <dt className="text-xs text-muted-foreground">{m.label}</dt>
            <dd className="text-lg font-semibold tabular-nums">{m.format(target)}</dd>
            {actual !== null && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs tabular-nums">
                  <DrillableValue
                    disabled={!drillMetric || !onDrillDown}
                    onClick={() => drillMetric && onDrillDown?.(drillMetric, m.label)}
                  >
                    <span className={cn(GOAL_TEXT_YTD[status])}>{m.format(actual)} actual</span>
                  </DrillableValue>
                  {pct !== null && (
                    <span className={cn("font-medium", GOAL_TEXT_YTD[status])}>{pct}%</span>
                  )}
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/60">
                  <div
                    className={cn(
                      "h-full rounded-full transition-[width] duration-500",
                      GOAL_BAR[status],
                    )}
                    style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </dl>
  );
}

// ── Annual Goals Card ────────────────────────────────────────────────

function AnnualGoalsCard({
  actuals,
  targets,
  onDrillDown,
  hideGpm = false,
}: {
  actuals: AnnualGoals;
  targets: Record<string, number>;
  onDrillDown?: (metric: DrillDownRequest["metric"], label: string) => void;
  hideGpm?: boolean;
}) {
  const metrics = buildMetricsList(actuals, hideGpm);
  const normalizedTargets = normalizeTargets(targets);
  const hasAnyTarget = metrics.some((m) => normalizedTargets[m.key] !== undefined);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          Annual Goals
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasAnyTarget ? (
          <p className="text-sm text-muted-foreground">
            No annual goals configured for this fiscal year.
          </p>
        ) : (
          <MetricTargetsGrid
            actuals={actuals}
            targets={targets}
            onDrillDown={onDrillDown}
            hideGpm={hideGpm}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Monthly Tracker Table ────────────────────────────────────────────

function MonthlyTrackerTable({
  rows,
  totals,
  onCellClick,
  hideGpm = false,
  targets,
}: {
  rows: MonthlyRow[];
  totals: AnnualGoals;
  hideGpm?: boolean;
  targets?: Record<string, number>;
  onCellClick?: (metric: DrillDownRequest["metric"], label: string, month: string) => void;
}) {
  const allMetricRows: {
    label: string;
    key: string;
    drillMetric: DrillDownRequest["metric"] | null;
    getValue: (r: MonthlyRow) => string;
    getTotal: () => string;
  }[] = [
    {
      label: "Opportunities",
      key: "opportunities",
      drillMetric: "opportunities",
      getValue: (r) => String(r.opportunities),
      getTotal: () => formatNumber(totals.opportunities),
    },
    {
      label: "Closed Deals",
      key: "closedDeals",
      drillMetric: "closedDeals",
      getValue: (r) => String(r.closedDeals),
      getTotal: () => formatNumber(totals.closedDeals),
    },
    {
      label: "Error %",
      key: "errorRate",
      drillMetric: null,
      getValue: (r) => formatPercent(r.errorRate),
      getTotal: () => formatPercent(totals.errorRate),
    },
    {
      label: "Gross Sales",
      key: "grossSales",
      drillMetric: "grossSales",
      getValue: (r) => formatCurrency(r.grossSales),
      getTotal: () => formatCurrency(totals.grossSales),
    },
    {
      label: "Close %",
      key: "closeRate",
      drillMetric: null,
      getValue: (r) => formatPercent(r.closeRate),
      getTotal: () => formatPercent(totals.closeRate),
    },
    {
      label: "GPM",
      key: "grossProfitMargin",
      drillMetric: "gpm",
      getValue: (r) => formatPercent(r.grossProfitMargin),
      getTotal: () => formatPercent(totals.grossProfitMargin),
    },
    {
      label: "AOV",
      key: "avgOrderValue",
      drillMetric: null,
      getValue: (r) => formatCurrency(r.avgOrderValue),
      getTotal: () => formatCurrency(totals.avgOrderValue),
    },
  ];

  // Map camelCase tracker keys to snake_case target keys
  const trackerKeyToTargetKey: Record<string, string> = {
    opportunities: "opportunities",
    closedDeals: "closed_deals",
    errorRate: "error_rate",
    grossSales: "gross_sales",
    closeRate: "close_rate",
    grossProfitMargin: "gross_profit_margin",
    avgOrderValue: "avg_order_value",
  };

  const shouldHideGpm = hideGpm || (targets && !("gross_profit_margin" in targets));
  const hasTargets = targets && Object.keys(targets).length > 0;
  const metricRows = allMetricRows.filter((m) => {
    if (shouldHideGpm && m.key === "grossProfitMargin") return false;
    if (!hasTargets) return true;
    const targetKey = trackerKeyToTargetKey[m.key];
    return targetKey ? targetKey in targets : true;
  });

  if (metricRows.length === 0) return null;

  // Relational vs Non-Relational itemization — rendered only when the server supplied
  // the split (Chase's dashboard). Non-Relational = total − relational so the two
  // buckets always sum to the parent Opportunities / Closed Deals totals above them.
  const hasRelationalSplit = rows.some((r) => r.relationalOpportunities !== undefined);
  const sumBy = (fn: (r: MonthlyRow) => number) => rows.reduce((s, r) => s + fn(r), 0);
  // GPM is a ratio, so it can't be summed across months or split by subtraction.
  // Recover the underlying dollars (gross sales × GPM = gross profit) for Relational and
  // Non-Relational, then divide the summed dollars to get a correctly weighted GPM.
  const relGross = (r: MonthlyRow) => r.relationalGrossSales ?? 0;
  const relGrossProfit = (r: MonthlyRow) => (r.relationalGrossProfitMargin ?? 0) * relGross(r);
  const nonRelGross = (r: MonthlyRow) => r.grossSales - relGross(r);
  const nonRelGrossProfit = (r: MonthlyRow) =>
    r.grossProfitMargin * r.grossSales - relGrossProfit(r);
  const ratio = (num: number, den: number) => (den > 0 ? num / den : 0);
  const relationalSubRows: Record<string, typeof allMetricRows> = {
    opportunities: [
      {
        label: "Relational Leads",
        key: "relationalLeads",
        drillMetric: null,
        getValue: (r) => String(r.relationalOpportunities ?? 0),
        getTotal: () => formatNumber(sumBy((r) => r.relationalOpportunities ?? 0)),
      },
      {
        label: "Non-Relational Leads",
        key: "nonRelationalLeads",
        drillMetric: null,
        getValue: (r) => String(r.opportunities - (r.relationalOpportunities ?? 0)),
        getTotal: () =>
          formatNumber(sumBy((r) => r.opportunities - (r.relationalOpportunities ?? 0))),
      },
    ],
    closedDeals: [
      {
        label: "Relational Closed Deals",
        key: "relationalClosed",
        drillMetric: null,
        getValue: (r) => String(r.relationalClosedDeals ?? 0),
        getTotal: () => formatNumber(sumBy((r) => r.relationalClosedDeals ?? 0)),
      },
      {
        label: "Non-Relational Closed Deals",
        key: "nonRelationalClosed",
        drillMetric: null,
        getValue: (r) => String(r.closedDeals - (r.relationalClosedDeals ?? 0)),
        getTotal: () => formatNumber(sumBy((r) => r.closedDeals - (r.relationalClosedDeals ?? 0))),
      },
    ],
    grossSales: [
      {
        label: "Relational Gross Sales",
        key: "relationalGrossSales",
        drillMetric: null,
        getValue: (r) => formatCurrency(r.relationalGrossSales ?? 0),
        getTotal: () => formatCurrency(sumBy((r) => r.relationalGrossSales ?? 0)),
      },
      {
        label: "Non-Relational Gross Sales",
        key: "nonRelationalGrossSales",
        drillMetric: null,
        getValue: (r) => formatCurrency(r.grossSales - (r.relationalGrossSales ?? 0)),
        getTotal: () => formatCurrency(sumBy((r) => r.grossSales - (r.relationalGrossSales ?? 0))),
      },
    ],
    grossProfitMargin: [
      {
        label: "Relational Gross Profit",
        key: "relationalGrossProfit",
        drillMetric: null,
        getValue: (r) => formatCurrency(relGrossProfit(r)),
        getTotal: () => formatCurrency(sumBy(relGrossProfit)),
      },
      {
        label: "Non-Relational Gross Profit",
        key: "nonRelationalGrossProfit",
        drillMetric: null,
        getValue: (r) => formatCurrency(nonRelGrossProfit(r)),
        getTotal: () => formatCurrency(sumBy(nonRelGrossProfit)),
      },
      {
        label: "Relational GPM",
        key: "relationalGpm",
        drillMetric: null,
        getValue: (r) => formatPercent(r.relationalGrossProfitMargin ?? 0),
        getTotal: () => formatPercent(ratio(sumBy(relGrossProfit), sumBy(relGross))),
      },
      {
        label: "Non-Relational GPM",
        key: "nonRelationalGpm",
        drillMetric: null,
        getValue: (r) => formatPercent(ratio(nonRelGrossProfit(r), nonRelGross(r))),
        getTotal: () => formatPercent(ratio(sumBy(nonRelGrossProfit), sumBy(nonRelGross))),
      },
    ],
  };
  const relationalSubRowKeys = new Set(
    Object.values(relationalSubRows)
      .flat()
      .map((r) => r.key),
  );
  const displayRows = hasRelationalSplit
    ? metricRows.flatMap((m) => [...(relationalSubRows[m.key] ?? []), m])
    : metricRows;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Monthly Tracker
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-xs uppercase tracking-wider sticky left-0 bg-card z-10 border-r">
                  Metric
                </TableHead>
                {rows.map((r) => (
                  <TableHead
                    key={r.month}
                    className="text-right text-xs uppercase tracking-wider whitespace-nowrap"
                  >
                    {formatMonthLabel(r.month)}
                  </TableHead>
                ))}
                <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l-2">
                  Total
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRows.map((metric) => {
                // On Chase's tracker (relational split present), bold the summary rows
                // so they stand out from the Relational / Non-Relational breakdown rows.
                const isSubRow = relationalSubRowKeys.has(metric.key);
                const isTotalRow = hasRelationalSplit && !isSubRow;
                return (
                  <TableRow key={metric.label} className="hover:bg-muted/30">
                    <TableCell
                      className={cn(
                        "text-xs sticky left-0 bg-card z-10 border-r whitespace-nowrap",
                        isTotalRow ? "font-bold" : "font-medium",
                        hasRelationalSplit && isSubRow && "font-normal text-muted-foreground",
                      )}
                    >
                      {metric.label}
                    </TableCell>
                    {rows.map((r) => (
                      <TableCell
                        key={r.month}
                        className={cn(
                          "text-right tabular-nums text-xs whitespace-nowrap",
                          isTotalRow && "font-bold",
                        )}
                      >
                        <DrillableValue
                          disabled={!metric.drillMetric || !onCellClick}
                          onClick={() =>
                            metric.drillMetric &&
                            onCellClick?.(metric.drillMetric, metric.label, r.month)
                          }
                        >
                          {metric.getValue(r)}
                        </DrillableValue>
                      </TableCell>
                    ))}
                    <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
                      {metric.getTotal()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Commission Config Card ───────────────────────────────────────────

function CommissionConfigCard({ config }: { config: CommissionConfig }) {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.04] to-transparent pointer-events-none" />
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Commission Config
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {config.quarterlyBaseline > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Quarterly Baseline</dt>
              <dd className="text-lg font-bold tabular-nums">
                {formatNumber(config.quarterlyBaseline)}
              </dd>
            </div>
          )}
          {config.perDeal > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Per-Deal Rate</dt>
              <dd className="text-lg font-bold tabular-nums">{formatCurrency(config.perDeal)}</dd>
            </div>
          )}
          {config.errorRateThreshold > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Error Threshold</dt>
              <dd className="text-lg font-bold tabular-nums">{config.errorRateThreshold}%</dd>
            </div>
          )}
          {config.nonRelationalGpmThreshold != null && (
            <div>
              <dt className="text-xs text-muted-foreground">NR GPM Threshold</dt>
              <dd className="text-lg font-bold tabular-nums">
                {config.nonRelationalGpmThreshold}%
              </dd>
            </div>
          )}
          {config.relationalGpmThreshold != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Relational GPM Threshold</dt>
              <dd className="text-lg font-bold tabular-nums">{config.relationalGpmThreshold}%</dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

// ── Quarter Commission Card ──────────────────────────────────────────

function QuarterCard({
  quarter,
  config,
  onDrillDown,
}: {
  quarter: QuarterCommission;
  config: CommissionConfig;
  onDrillDown?: (metric: DrillDownRequest["metric"], label: string) => void;
}) {
  const hasPayout = quarter.payOut > 0;
  const eligible = Math.max(0, quarter.closedDeals - config.quarterlyBaseline - quarter.errorsMade);

  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-shadow",
        hasPayout && "ring-1 ring-accent/30 shadow-md",
      )}
    >
      {hasPayout && (
        <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] to-transparent pointer-events-none" />
      )}
      <CardHeader className="pb-2">
        <CardTitle className="text-center font-heading text-xl tracking-tight">
          {quarter.label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Closed Deals
                <HelpTip text="Total deals closed this quarter" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("closedDeals", `Closed Deals — ${quarter.label}`)}
                >
                  {quarter.closedDeals}
                </DrillableValue>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Baseline
                <HelpTip text="Minimum deals required before commission kicks in" />
              </dt>
              <dd className="font-semibold tabular-nums">{config.quarterlyBaseline}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Errors
                <HelpTip text="Consultant accuracy errors that reduce eligible deals" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("errors", `Errors — ${quarter.label}`)}
                >
                  {quarter.errorsMade}
                </DrillableValue>
              </dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium flex items-center gap-1">
                Eligible
                <HelpTip text="Closed Deals − Baseline − Errors" />
              </dt>
              <dd
                className={cn("font-bold tabular-nums", eligible === 0 && "text-muted-foreground")}
              >
                {eligible}
              </dd>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <dt>
                {eligible} × {formatCurrency(config.perDeal)}
              </dt>
              <dd className="tabular-nums">{formatCurrency(eligible * config.perDeal)}</dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between items-center">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                Payout
                <HelpTip text="Eligible × Per-Deal Rate" />
              </dt>
              <dd
                className={cn(
                  "text-lg font-bold tabular-nums tracking-tight",
                  hasPayout ? "text-accent-foreground" : "text-muted-foreground",
                )}
              >
                {formatCurrency(quarter.payOut)}
              </dd>
            </div>
          </dl>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

// ── NR Commission Config Card ────────────────────────────────────────

function NRCommissionConfigCard({
  config,
}: {
  config: {
    quarterlyBaseline: number;
    nonRelationalGpmThreshold: number | null;
    relationalGpmThreshold: number | null;
    errorRateThreshold: number;
  };
}) {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.04] to-transparent pointer-events-none" />
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Consultant Commission
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {config.quarterlyBaseline > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Quarterly Baseline</dt>
              <dd className="text-lg font-bold tabular-nums">
                {formatNumber(config.quarterlyBaseline)}
              </dd>
            </div>
          )}
          {config.nonRelationalGpmThreshold != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Non Relational Gross Profit Margin</dt>
              <dd className="text-lg font-bold tabular-nums">
                {config.nonRelationalGpmThreshold}%
              </dd>
            </div>
          )}
          {config.relationalGpmThreshold != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Relational Gross Profit Margin</dt>
              <dd className="text-lg font-bold tabular-nums">{config.relationalGpmThreshold}%</dd>
            </div>
          )}
          {config.errorRateThreshold > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Error Rate</dt>
              <dd className="text-lg font-bold tabular-nums">
                Less than {config.errorRateThreshold}%
              </dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

// ── NR Quarter Commission Card ──────────────────────────────────────

function NRQuarterCard({
  quarter,
  baseline,
  nrGpmThreshold,
  onDrillDown,
}: {
  quarter: NRQuarterCommission;
  baseline: number;
  nrGpmThreshold: number;
  onDrillDown?: (metric: DrillDownRequest["metric"], label: string) => void;
}) {
  const hasPayout = quarter.payOut > 0;

  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-shadow",
        hasPayout && "ring-1 ring-accent/30 shadow-md",
      )}
    >
      {hasPayout && (
        <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] to-transparent pointer-events-none" />
      )}
      <CardHeader className="pb-2">
        <CardTitle className="text-center font-heading text-xl tracking-tight">
          {quarter.label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Orders
                <HelpTip text="Closed deals from non-relational sources this quarter" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("closedDeals", `NR Total Orders — ${quarter.label}`)}
                >
                  {quarter.totalOrders}
                </DrillableValue>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Errors Made
                <HelpTip text="Consultant accuracy errors" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("errors", `NR Errors — ${quarter.label}`)}
                >
                  {quarter.totalErrors}
                </DrillableValue>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Gross Profit
                <HelpTip text="Sum of (net price − cost per deal) for NR deals this quarter" />
              </dt>
              <dd className="font-semibold tabular-nums">
                {formatCurrency(quarter.totalGrossProfit)}
              </dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium flex items-center gap-1">
                Average Gross Profit per Deal
                <HelpTip text="Total Gross Profit ÷ Total Orders" />
              </dt>
              <dd className="font-bold tabular-nums text-accent-foreground">
                {formatCurrency(quarter.averageGpm)}
              </dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium flex items-center gap-1">
                Eligible Commission
                <HelpTip
                  text={`Total Orders (${quarter.totalOrders}) − Errors (${quarter.totalErrors}) − Baseline (${baseline})`}
                />
              </dt>
              <dd
                className={cn(
                  "font-bold tabular-nums",
                  quarter.eligible === 0 && "text-muted-foreground",
                )}
              >
                {quarter.eligible}
              </dd>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <dt>
                {formatCurrency(quarter.averageGpm)} × {quarter.eligible} × {nrGpmThreshold}%
              </dt>
              <dd className="tabular-nums">{formatCurrency(quarter.payOut)}</dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between items-center">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                Pay Out
                <HelpTip text="Average Gross Profit per Deal × Eligible × NR GPM Threshold" />
              </dt>
              <dd
                className={cn(
                  "text-lg font-bold tabular-nums tracking-tight",
                  hasPayout ? "text-accent-foreground" : "text-muted-foreground",
                )}
              >
                {formatCurrency(quarter.payOut)}
              </dd>
            </div>
          </dl>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

// ── Blended (Relational + Non-Relational) Quarter Card ──────────────

function BlendedQuarterCard({
  quarter,
  baseline,
  onDrillDown,
}: {
  quarter: BlendedQuarterCommission;
  baseline: number;
  onDrillDown?: (metric: DrillDownRequest["metric"], label: string) => void;
}) {
  const hasPayout = quarter.payOut > 0;

  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-shadow",
        hasPayout && "ring-1 ring-accent/30 shadow-md",
      )}
    >
      {hasPayout && (
        <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] to-transparent pointer-events-none" />
      )}
      <CardHeader className="pb-2">
        <CardTitle className="text-center font-heading text-xl tracking-tight">
          {quarter.label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Closed Deals
                <HelpTip text="Every deal he closed this quarter, all sources" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("closedDeals", `Closed Deals — ${quarter.label}`)}
                >
                  {quarter.totalClosed}
                </DrillableValue>
              </dd>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <dt>
                Relational {quarter.relClosed} · Non-Relational {quarter.nonRelClosed}
              </dt>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Errors Made
                <HelpTip text="Consultant accuracy errors" />
              </dt>
              <dd className="font-semibold tabular-nums">{quarter.errorsMade}</dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium flex items-center gap-1">
                Eligible Commission
                <HelpTip
                  text={`Total Closed (${quarter.totalClosed}) − Errors (${quarter.errorsMade}) − Baseline (${baseline}), split by his deal mix`}
                />
              </dt>
              <dd
                className={cn(
                  "font-bold tabular-nums",
                  quarter.eligible === 0 && "text-muted-foreground",
                )}
              >
                {quarter.eligible}
              </dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium">
                Non-Relational ({quarter.nonRelEligible} @ {quarter.nonRelRatePercent}%)
              </dt>
              <dd className="font-semibold tabular-nums">{formatCurrency(quarter.nonRelPayOut)}</dd>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <dt>
                {formatCurrency(quarter.nonRelAvgGp)} × {quarter.nonRelEligible} ×{" "}
                {quarter.nonRelRatePercent}%
              </dt>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium">
                Relational ({quarter.relEligible} @ {quarter.relRatePercent}%)
              </dt>
              <dd className="font-semibold tabular-nums">{formatCurrency(quarter.relPayOut)}</dd>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <dt>
                {formatCurrency(quarter.relAvgGp)} × {quarter.relEligible} ×{" "}
                {quarter.relRatePercent}%
              </dt>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between items-center">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                Pay Out
                <HelpTip text="Non-Relational (deals × avg gross profit × NR %) + Relational (deals × avg gross profit × Rel %)" />
              </dt>
              <dd
                className={cn(
                  "text-lg font-bold tabular-nums tracking-tight",
                  hasPayout ? "text-accent-foreground" : "text-muted-foreground",
                )}
              >
                {formatCurrency(quarter.payOut)}
              </dd>
            </div>
          </dl>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

// ── Standard Margin Commission Config Card ──────────────────────────

function SMCommissionConfigCard({
  config,
}: {
  config: {
    quarterlyBaseline: number;
    standardMarginGpmThreshold: number | null;
    errorRateThreshold: number;
  };
}) {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.04] to-transparent pointer-events-none" />
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Consultant Commission
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {config.quarterlyBaseline > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Quarterly Baseline</dt>
              <dd className="text-lg font-bold tabular-nums">
                {formatNumber(config.quarterlyBaseline)}
              </dd>
            </div>
          )}
          {config.standardMarginGpmThreshold != null && (
            <div>
              <dt className="text-xs text-muted-foreground">Gross Profit Margin</dt>
              <dd className="text-lg font-bold tabular-nums">
                {config.standardMarginGpmThreshold}%
              </dd>
            </div>
          )}
          {config.errorRateThreshold > 0 && (
            <div>
              <dt className="text-xs text-muted-foreground">Error Rate</dt>
              <dd className="text-lg font-bold tabular-nums">
                Less than {config.errorRateThreshold}%
              </dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

// ── Standard Margin Quarter Commission Card ─────────────────────────

function SMQuarterCard({
  quarter,
  baseline,
  smGpmThreshold,
  note,
  onDrillDown,
}: {
  quarter: NRQuarterCommission;
  baseline: number;
  smGpmThreshold: number;
  /** Small line under the quarter title, e.g. when the quarter is on new terms. */
  note?: string;
  onDrillDown?: (metric: DrillDownRequest["metric"], label: string) => void;
}) {
  const hasPayout = quarter.payOut > 0;

  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-shadow",
        hasPayout && "ring-1 ring-accent/30 shadow-md",
      )}
    >
      {hasPayout && (
        <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] to-transparent pointer-events-none" />
      )}
      <CardHeader className="pb-2">
        <CardTitle className="text-center font-heading text-xl tracking-tight">
          {quarter.label}
        </CardTitle>
        {note && <p className="text-center text-xs font-medium text-accent">{note}</p>}
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Orders
                <HelpTip text="Closed deals this quarter" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("closedDeals", `SM Total Orders — ${quarter.label}`)}
                >
                  {quarter.totalOrders}
                </DrillableValue>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Errors Made
                <HelpTip text="Consultant accuracy errors" />
              </dt>
              <dd className="font-semibold tabular-nums">
                <DrillableValue
                  disabled={!onDrillDown}
                  onClick={() => onDrillDown?.("errors", `SM Errors — ${quarter.label}`)}
                >
                  {quarter.totalErrors}
                </DrillableValue>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground flex items-center gap-1">
                Total Gross Profit
                <HelpTip text="Sum of (net price − cost per deal) for all deals this quarter" />
              </dt>
              <dd className="font-semibold tabular-nums">
                {formatCurrency(quarter.totalGrossProfit)}
              </dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium flex items-center gap-1">
                Average Gross Profit per Deal
                <HelpTip text="Total Gross Profit ÷ Total Orders" />
              </dt>
              <dd className="font-bold tabular-nums text-accent-foreground">
                {formatCurrency(quarter.averageGpm)}
              </dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between">
              <dt className="text-muted-foreground font-medium flex items-center gap-1">
                Eligible Commission
                <HelpTip
                  text={`Total Orders (${quarter.totalOrders}) − Errors (${quarter.totalErrors}) − Baseline (${baseline})`}
                />
              </dt>
              <dd
                className={cn(
                  "font-bold tabular-nums",
                  quarter.eligible === 0 && "text-muted-foreground",
                )}
              >
                {quarter.eligible}
              </dd>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <dt>
                {formatCurrency(quarter.averageGpm)} × {quarter.eligible} × {smGpmThreshold}%
              </dt>
              <dd className="tabular-nums">{formatCurrency(quarter.payOut)}</dd>
            </div>
            <Separator className="my-1" />
            <div className="flex justify-between items-center">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                Pay Out
                <HelpTip text="Average Gross Profit per Deal × Eligible × GPM Threshold" />
              </dt>
              <dd
                className={cn(
                  "text-lg font-bold tabular-nums tracking-tight",
                  hasPayout ? "text-accent-foreground" : "text-muted-foreground",
                )}
              >
                {formatCurrency(quarter.payOut)}
              </dd>
            </div>
          </dl>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

// ── Help Tooltip ────────────────────────────────────────────────────

function HelpTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CircleHelp className="h-3 w-3 text-muted-foreground/40 hover:text-muted-foreground cursor-help" />
      </TooltipTrigger>
      <TooltipContent side="top">{text}</TooltipContent>
    </Tooltip>
  );
}

// ── Shared Tracker Utilities ─────────────────────────────────────────

interface TrackerMetric {
  label: string;
  drillMetric: DrillDownRequest["metric"] | null;
  getValue: (r: MonthlyRow) => string;
  getTotal: () => string;
}

function buildTrackerMetrics(
  prefix: string,
  actuals: AnnualGoals,
  hideGpm = false,
  targets?: Record<string, number>,
): TrackerMetric[] {
  const list: TrackerMetric[] = [
    {
      label: `${prefix} Lead Generation`,
      drillMetric: "opportunities",
      getValue: (r) => String(r.opportunities),
      getTotal: () => formatNumber(actuals.opportunities),
    },
    {
      label: `${prefix} Closed Deals`,
      drillMetric: "closedDeals",
      getValue: (r) => String(r.closedDeals),
      getTotal: () => formatNumber(actuals.closedDeals),
    },
    {
      label: `${prefix} Error %`,
      drillMetric: null,
      getValue: (r) => formatPercent(r.errorRate),
      getTotal: () => formatPercent(actuals.errorRate),
    },
    {
      label: `${prefix} Gross Sales`,
      drillMetric: "grossSales",
      getValue: (r) => formatCurrency(r.grossSales),
      getTotal: () => formatCurrency(actuals.grossSales),
    },
    {
      label: `${prefix} GPM`,
      drillMetric: "gpm",
      getValue: (r) => formatPercent(r.grossProfitMargin),
      getTotal: () => formatPercent(actuals.grossProfitMargin),
    },
    {
      label: `${prefix} Close Rate`,
      drillMetric: null,
      getValue: (r) => formatPercent(r.closeRate),
      getTotal: () => formatPercent(actuals.closeRate),
    },
    {
      label: `${prefix} AOV`,
      drillMetric: null,
      getValue: (r) => formatCurrency(r.avgOrderValue),
      getTotal: () => formatCurrency(actuals.avgOrderValue),
    },
  ];
  const trackerLabelToTargetKey: Record<string, string> = {
    "Lead Generation": "opportunities",
    "Closed Deals": "closed_deals",
    "Error %": "error_rate",
    "Gross Sales": "gross_sales",
    GPM: "gross_profit_margin",
    "Close Rate": "close_rate",
    AOV: "avg_order_value",
  };

  const shouldHideGpm = hideGpm || (targets && !("gross_profit_margin" in targets));
  const hasTargets = targets && Object.keys(targets).length > 0;
  return list.filter((m) => {
    if (shouldHideGpm && m.label.endsWith("GPM")) return false;
    if (!hasTargets) return true;
    const suffix = m.label.replace(`${prefix} `, "");
    const targetKey = trackerLabelToTargetKey[suffix];
    return targetKey ? targetKey in targets : true;
  });
}

function MetricMonthlyTrackerTable({
  title,
  metrics,
  monthlyData,
  months,
  onCellClick,
  headerContent,
}: {
  title: string;
  metrics: TrackerMetric[];
  monthlyData: MonthlyRow[];
  months: string[];
  onCellClick?: (metric: DrillDownRequest["metric"], label: string, month: string) => void;
  headerContent?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </CardTitle>
        {headerContent}
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-xs uppercase tracking-wider sticky left-0 bg-card z-10 border-r">
                  Metric
                </TableHead>
                {months.map((m) => (
                  <TableHead
                    key={m}
                    className="text-right text-xs uppercase tracking-wider whitespace-nowrap"
                  >
                    {formatMonthLabel(m)}
                  </TableHead>
                ))}
                <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l-2">
                  Total
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {metrics.map((metric) => (
                <TableRow key={metric.label} className="hover:bg-muted/30">
                  <TableCell className="text-xs font-medium sticky left-0 bg-card z-10 border-r whitespace-nowrap">
                    {metric.label}
                  </TableCell>
                  {monthlyData.map((r) => (
                    <TableCell
                      key={r.month}
                      className="text-right tabular-nums text-xs whitespace-nowrap"
                    >
                      <DrillableValue
                        disabled={!metric.drillMetric || !onCellClick}
                        onClick={() =>
                          metric.drillMetric &&
                          onCellClick?.(metric.drillMetric, metric.label, r.month)
                        }
                      >
                        {metric.getValue(r)}
                      </DrillableValue>
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
                    {metric.getTotal()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/** Create drill-down handlers for a goal section (NR, Aff, Source). */
function createSectionDrillHandlers(
  onDrillDown: ((req: DrillDownRequest) => void) | undefined,
  base: Omit<DrillDownRequest, "metric" | "label" | "month">,
  labelPrefix: string,
) {
  const handleGridDrill = (metric: DrillDownRequest["metric"], label: string) => {
    onDrillDown?.({ ...base, metric, label: `${labelPrefix} ${label}` });
  };
  const handleCellClick = (metric: DrillDownRequest["metric"], label: string, month: string) => {
    onDrillDown?.({ ...base, metric, month, label: `${label} — ${formatMonthLabel(month)}` });
  };
  return { handleGridDrill, handleCellClick };
}

// ── Executive Dashboard Section ──────────────────────────────────────

function ExecutiveDashboardSection({
  data,
  onDrillDown,
}: {
  data: ExecutiveGoalsData;
  onDrillDown?: (req: DrillDownRequest) => void;
}) {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  const { channels, channelBreakdowns } = data;
  const active = (selectedChannel && channelBreakdowns?.[selectedChannel]) || {
    actuals: data.actuals,
    monthlyTracker: data.monthlyTracker,
    sources: undefined as string[] | undefined,
  };
  const { actuals: activeActuals, monthlyTracker: activeMonthly, sources: activeSources } = active;

  const months = activeMonthly.map((r) => r.month);
  const hasChannels =
    channels.length > 0 && channelBreakdowns && Object.keys(channelBreakdowns).length > 0;

  const trackerMetrics: TrackerMetric[] = [
    {
      label: "Opportunities",
      drillMetric: "opportunities",
      getValue: (r) => formatNumber(r.opportunities),
      getTotal: () => formatNumber(activeActuals.opportunities),
    },
    {
      label: "Closed Deals",
      drillMetric: "closedDeals",
      getValue: (r) => formatNumber(r.closedDeals),
      getTotal: () => formatNumber(activeActuals.closedDeals),
    },
    {
      label: "Error %",
      drillMetric: "errors",
      getValue: (r) => formatPercent(r.errorRate),
      getTotal: () => formatPercent(activeActuals.errorRate),
    },
    {
      label: "Close Rate",
      drillMetric: null,
      getValue: (r) => formatPercent(r.closeRate),
      getTotal: () => formatPercent(activeActuals.closeRate),
    },
    {
      label: "Avg Order Value",
      drillMetric: null,
      getValue: (r) => formatCurrency(r.avgOrderValue),
      getTotal: () => formatCurrency(activeActuals.avgOrderValue),
    },
    {
      label: "Gross Sales",
      drillMetric: "grossSales",
      getValue: (r) => formatCurrency(r.grossSales),
      getTotal: () => formatCurrency(activeActuals.grossSales),
    },
    {
      label: "Net Sales",
      drillMetric: "netSales",
      getValue: (r) => formatCurrency(r.netSales),
      getTotal: () => formatCurrency(activeActuals.netSales),
    },
    {
      // Net Sales as a share of Gross Sales (i.e. how much of the sale sticks after discounts/fees).
      label: "Net Sale %",
      drillMetric: null,
      getValue: (r) => formatPercent(r.grossSales > 0 ? r.netSales / r.grossSales : 0),
      getTotal: () =>
        formatPercent(
          activeActuals.grossSales > 0 ? activeActuals.netSales / activeActuals.grossSales : 0,
        ),
    },
    {
      // Gross Profit dollars = Gross Profit Margin × Gross Sales (GPM is measured against Gross Sales).
      label: "Gross Profit",
      drillMetric: "gpm",
      getValue: (r) => formatCurrency(r.grossProfitMargin * r.grossSales),
      getTotal: () => formatCurrency(activeActuals.grossProfitMargin * activeActuals.grossSales),
    },
    {
      label: "Gross Profit Margin",
      drillMetric: "gpm",
      getValue: (r) => formatPercent(r.grossProfitMargin),
      getTotal: () => formatPercent(activeActuals.grossProfitMargin),
    },
  ];

  const handleCellClick = (metric: DrillDownRequest["metric"], label: string, month: string) => {
    onDrillDown?.({
      metric,
      section: "main",
      companyWide: true,
      sources: activeSources,
      month,
      label: `${label} — ${formatMonthLabel(month)}`,
    });
  };

  const title = selectedChannel
    ? `${selectedChannel} Monthly Performance`
    : "Company-Wide Monthly Performance";

  return (
    <MetricMonthlyTrackerTable
      title={title}
      metrics={trackerMetrics}
      monthlyData={activeMonthly}
      months={months}
      onCellClick={handleCellClick}
      headerContent={
        hasChannels ? (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <Badge
              variant={selectedChannel === null ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setSelectedChannel(null)}
            >
              All
            </Badge>
            {channels.map((ch) => (
              <Badge
                key={ch}
                variant={selectedChannel === ch ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => setSelectedChannel(ch === selectedChannel ? null : ch)}
              >
                {ch}
              </Badge>
            ))}
          </div>
        ) : undefined
      }
    />
  );
}

// ── FP Reporting Section ─────────────────────────────────────────────

function FPReportingSection({
  data,
  monthlyTracker,
  onDrillDown,
}: {
  data: FPReportingGoalsData;
  monthlyTracker: MonthlyRow[];
  onDrillDown?: (req: DrillDownRequest) => void;
}) {
  const {
    targets,
    actuals,
    monthlyTracker: fpMonthly,
    digitalSources,
    digitalSourceBreakdowns,
  } = data;
  const months = monthlyTracker.map((r) => r.month);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);

  const hasSourcePills = digitalSources.length > 1 && !!digitalSourceBreakdowns;
  const sourceData = selectedSource ? digitalSourceBreakdowns?.[selectedSource] : null;

  // Resolve which sources apply to drill-down based on the selected pill
  const activeDigitalSources = selectedSource ? [selectedSource] : digitalSources;

  const fpMetricsDefs: {
    key: string;
    label: string;
    actual: number | null;
    target: number | undefined;
    format: (v: number) => string;
    drillMetric?: DrillDownRequest["metric"];
    drillSources?: string[];
  }[] = [
    {
      key: "lead_generation",
      label: "FP Lead Generation",
      actual: actuals.leadGeneration,
      target: targets.lead_generation,
      format: formatNumber,
      drillMetric: "opportunities",
      // No sources filter — company-wide total lead generation
    },
    {
      key: "digital_lead_generation",
      label: "FP Digital Total Leads",
      actual: actuals.digitalLeadGeneration,
      target: targets.digital_lead_generation,
      format: formatNumber,
      drillMetric: "opportunities",
      drillSources: activeDigitalSources,
    },
    {
      key: "instagram_lead_generation",
      label: "FP Instagram Lead Generation",
      actual: digitalSourceBreakdowns?.Instagram?.actuals.digitalLeadGeneration ?? null,
      target: targets.instagram_lead_generation,
      format: formatNumber,
      drillMetric: "opportunities",
      drillSources: ["Instagram"],
    },
    {
      key: "tiktok_lead_generation",
      label: "FP TikTok Lead Generation",
      actual: digitalSourceBreakdowns?.TikTok?.actuals.digitalLeadGeneration ?? null,
      target: targets.tiktok_lead_generation,
      format: formatNumber,
      drillMetric: "opportunities",
      drillSources: ["TikTok"],
    },
    {
      key: "digital_cac",
      label: "FP Digital CAC",
      actual: null,
      target: targets.digital_cac,
      format: formatCurrency,
      drillMetric: "closedDeals",
      drillSources: activeDigitalSources,
    },
    {
      key: "digital_close_rate",
      label: "FP Digital Close Rate",
      actual: actuals.digitalCloseRate,
      target: targets.digital_close_rate,
      format: (v: number) => `${v.toFixed(1)}%`,
      drillMetric: "opportunities",
      drillSources: activeDigitalSources,
    },
    {
      key: "digital_gross_sales",
      label: "FP Digital Gross Sales",
      actual: actuals.digitalGrossSales,
      target: targets.digital_gross_sales,
      format: formatCurrency,
      drillMetric: "grossSales",
      drillSources: activeDigitalSources,
    },
  ];

  const hasAnyTarget = fpMetricsDefs.some((m) => m.target !== undefined);

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          FP Reporting
        </span>
      </div>

      {/* Annual Goals + Monthly Tracker */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              <Target className="h-3.5 w-3.5" />
              FP Outcome Annual Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!hasAnyTarget ? (
              <p className="text-sm text-muted-foreground">
                No FP reporting goals configured for this fiscal year.
              </p>
            ) : (
              <dl className="space-y-3">
                {fpMetricsDefs.map((m) => {
                  if (m.target === undefined) return null;
                  const actualVal = m.actual ?? 0;
                  const pct =
                    m.actual !== null && m.target > 0
                      ? Math.round((actualVal / m.target) * 100)
                      : null;
                  const met = m.actual !== null && m.target > 0 && actualVal >= m.target;
                  const status = goalStatus(m.actual, m.target);
                  return (
                    <div key={m.key} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <dt className="text-xs text-muted-foreground">{m.label}</dt>
                        <dd className="text-sm font-semibold tabular-nums">{m.format(m.target)}</dd>
                      </div>
                      <div className="flex items-center justify-between text-xs tabular-nums">
                        <span className={cn(GOAL_TEXT_YTD[status])}>
                          {m.actual !== null ? `${m.format(actualVal)} actual` : "N/A"}
                        </span>
                        {pct !== null && (
                          <span className={cn("font-medium", GOAL_TEXT_YTD[status])}>{pct}%</span>
                        )}
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/60">
                        <div
                          className={cn(
                            "h-full rounded-full transition-[width] duration-500",
                            GOAL_BAR[status],
                          )}
                          style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </dl>
            )}
            {digitalSources.length > 0 && (
              <p className="mt-4 text-xs italic text-muted-foreground">
                FP Digital = {digitalSources.join(", ")}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Monthly Tracker */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              {selectedSource ? `${selectedSource} Monthly Tracker` : "Monthly Tracker"}
            </CardTitle>
            {hasSourcePills && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-xs text-muted-foreground mr-1">FP Digital:</span>
                <Badge
                  variant={selectedSource === null ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setSelectedSource(null)}
                >
                  All
                </Badge>
                {digitalSources.map((source) => (
                  <Badge
                    key={source}
                    variant={selectedSource === source ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setSelectedSource(source === selectedSource ? null : source)}
                  >
                    {source}
                  </Badge>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent className="px-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs uppercase tracking-wider sticky left-0 bg-card z-10 border-r">
                      Metric
                    </TableHead>
                    {months.map((m) => (
                      <TableHead
                        key={m}
                        className="text-right text-xs uppercase tracking-wider whitespace-nowrap"
                      >
                        {formatMonthLabel(m)}
                      </TableHead>
                    ))}
                    <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l-2">
                      Total
                    </TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l">
                      Remaining
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fpMetricsDefs
                    .filter((d) => d.target !== undefined)
                    .map((def) => {
                      const digitalProp = FP_DIGITAL_KEY_MAP[def.key];
                      const sourceForKey =
                        def.key === "instagram_lead_generation"
                          ? "Instagram"
                          : def.key === "tiktok_lead_generation"
                            ? "TikTok"
                            : null;
                      const getMonthValue = (row: FPReportingMonthlyRow): number | null => {
                        if (sourceForKey) {
                          const srcRow = digitalSourceBreakdowns?.[
                            sourceForKey
                          ]?.monthlyTracker.find((s) => s.month === row.month);
                          return srcRow?.digitalLeadGeneration ?? 0;
                        }
                        if (sourceData && digitalProp) {
                          const srcRow = sourceData.monthlyTracker.find(
                            (s) => s.month === row.month,
                          );
                          return srcRow?.[digitalProp] ?? 0;
                        }
                        if (def.key === "lead_generation") return row.leadGeneration;
                        if (def.key === "digital_cac") return null;
                        return digitalProp ? row[digitalProp] : 0;
                      };
                      const total =
                        sourceData && digitalProp ? sourceData.actuals[digitalProp] : def.actual;
                      const remaining =
                        !selectedSource && def.target !== undefined && total !== null
                          ? Math.max(0, def.target - total)
                          : null;

                      return (
                        <TableRow key={def.key} className="hover:bg-muted/30">
                          <TableCell className="text-xs font-medium sticky left-0 bg-card z-10 border-r whitespace-nowrap">
                            {def.label}
                          </TableCell>
                          {fpMonthly.map((r) => {
                            const val = getMonthValue(r);
                            const canDrill = !!def.drillMetric && !!onDrillDown && val !== null;
                            return (
                              <TableCell
                                key={r.month}
                                className="text-right tabular-nums text-xs whitespace-nowrap"
                              >
                                <DrillableValue
                                  disabled={!canDrill}
                                  onClick={() => {
                                    if (!def.drillMetric || !onDrillDown) return;
                                    onDrillDown({
                                      metric: def.drillMetric,
                                      section: "source",
                                      companyWide: true,
                                      month: r.month,
                                      sources: def.drillSources,
                                      label: `${def.label} — ${formatMonthLabel(r.month)}`,
                                    });
                                  }}
                                >
                                  {val === null ? "—" : def.format(val)}
                                </DrillableValue>
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
                            {total !== null ? def.format(total) : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-xs border-l whitespace-nowrap">
                            {remaining !== null ? def.format(remaining) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// ── Created Reporting Section ────────────────────────────────────────

function CreatedReportingSection({
  data,
  monthlyTracker,
  onDrillDown,
}: {
  data: CreatedGoalsData;
  monthlyTracker: MonthlyRow[];
  onDrillDown?: (req: ShopifyDrillDownRequest) => void;
}) {
  const { targets, actuals, monthlyTracker: createdMonthly } = data;
  const months = monthlyTracker.map((r) => r.month);

  const createdMetricsDefs: {
    key: ShopifyDrillDownRequest["metric"];
    label: string;
    actual: number;
    target: number | undefined;
    format: (v: number) => string;
  }[] = [
    {
      key: "popup_units",
      label: "Pop Up Units",
      actual: actuals.popupUnits,
      target: targets.popup_units,
      format: formatNumber,
    },
    {
      key: "web_units",
      label: "Web Units",
      actual: actuals.webUnits,
      target: targets.web_units,
      format: formatNumber,
    },
    {
      key: "total_units",
      label: "Total Units",
      actual: actuals.totalUnits,
      target: targets.total_units,
      format: formatNumber,
    },
    {
      key: "gross_sales",
      label: "Created Gross Sales",
      actual: actuals.grossSales,
      target: targets.gross_sales,
      format: formatCurrency,
    },
    {
      key: "popup_gross_sales",
      label: "Pop Up Gross Sales",
      actual: actuals.popupGrossSales,
      target: targets.popup_gross_sales,
      format: formatCurrency,
    },
  ];

  const hasAnyTarget = createdMetricsDefs.some((m) => m.target !== undefined);

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          CREATED Reporting
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* KPI / Annual Goals */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              <Target className="h-3.5 w-3.5" />
              Created Outcome Annual Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!hasAnyTarget ? (
              <p className="text-sm text-muted-foreground">
                No created goals configured for this fiscal year.
              </p>
            ) : (
              <dl className="space-y-3">
                {createdMetricsDefs.map((m) => {
                  if (m.target === undefined) return null;
                  const pct = m.target > 0 ? Math.round((m.actual / m.target) * 100) : null;
                  const met = m.target > 0 && m.actual >= m.target;
                  const status = goalStatus(m.actual, m.target);
                  return (
                    <div key={m.key} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <dt className="text-xs text-muted-foreground">{m.label}</dt>
                        <dd className="text-sm font-semibold tabular-nums">{m.format(m.target)}</dd>
                      </div>
                      <div className="flex items-center justify-between text-xs tabular-nums">
                        <span className={cn(GOAL_TEXT_YTD[status])}>
                          {m.format(m.actual)} actual
                        </span>
                        {pct !== null && (
                          <span className={cn("font-medium", GOAL_TEXT_YTD[status])}>{pct}%</span>
                        )}
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/60">
                        <div
                          className={cn(
                            "h-full rounded-full transition-[width] duration-500",
                            GOAL_BAR[status],
                          )}
                          style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </dl>
            )}
          </CardContent>
        </Card>

        {/* Monthly Tracker */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Monthly Tracker
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs uppercase tracking-wider sticky left-0 bg-card z-10 border-r">
                      Metric
                    </TableHead>
                    {months.map((m) => (
                      <TableHead
                        key={m}
                        className="text-right text-xs uppercase tracking-wider whitespace-nowrap"
                      >
                        {formatMonthLabel(m)}
                      </TableHead>
                    ))}
                    <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l-2">
                      Total
                    </TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l">
                      Remaining
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {createdMetricsDefs
                    .filter((d) => d.target !== undefined || d.actual !== 0)
                    .map((def) => {
                      const getMonthValue = (row: CreatedOrderMonthlyRow) => {
                        switch (def.key) {
                          case "popup_units":
                            return row.popupUnits;
                          case "web_units":
                            return row.webUnits;
                          case "total_units":
                            return row.totalUnits;
                          case "gross_sales":
                            return row.grossSales;
                          case "popup_gross_sales":
                            return row.popupGrossSales;
                          default:
                            return 0;
                        }
                      };
                      const total = def.actual;
                      const remaining =
                        def.target !== undefined ? Math.max(0, def.target - total) : null;

                      // Monthly goals (e.g. created_total_units_m01..m12 / created_gross_sales_m01..m12)
                      const monthlyGoals: (number | undefined)[] = createdMonthly.map(
                        (_, i) => targets[`${def.key}_m${String(i + 1).padStart(2, "0")}`],
                      );
                      const hasMonthlyGoals = monthlyGoals.some((g) => g !== undefined);
                      const goalSum = monthlyGoals.reduce<number>((sum, g) => sum + (g ?? 0), 0);

                      return (
                        <Fragment key={def.key}>
                          <TableRow className="hover:bg-muted/30">
                            <TableCell className="text-xs font-medium sticky left-0 bg-card z-10 border-r whitespace-nowrap">
                              {def.label}
                            </TableCell>
                            {createdMonthly.map((r) => (
                              <TableCell
                                key={r.month}
                                className="text-right tabular-nums text-xs whitespace-nowrap"
                              >
                                <DrillableValue
                                  disabled={!onDrillDown}
                                  onClick={() =>
                                    onDrillDown?.({
                                      metric: def.key,
                                      month: r.month,
                                      label: `${def.label} — ${formatMonthLabel(r.month)}`,
                                    })
                                  }
                                >
                                  {def.key === "gross_sales" || def.key === "popup_gross_sales"
                                    ? formatCurrency(getMonthValue(r))
                                    : formatNumber(getMonthValue(r))}
                                </DrillableValue>
                              </TableCell>
                            ))}
                            <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
                              {def.format(total)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-xs border-l whitespace-nowrap">
                              {remaining !== null ? def.format(remaining) : "—"}
                            </TableCell>
                          </TableRow>
                          {hasMonthlyGoals && (
                            <TableRow key={`${def.key}-goal`} className="bg-muted/20">
                              <TableCell className="text-xs text-muted-foreground sticky left-0 bg-card z-10 border-r whitespace-nowrap pl-6">
                                Monthly Goal
                              </TableCell>
                              {createdMonthly.map((_, i) => {
                                const goal = monthlyGoals[i];
                                return (
                                  <TableCell
                                    key={months[i]}
                                    className="text-right tabular-nums text-xs text-muted-foreground whitespace-nowrap"
                                  >
                                    {goal !== undefined ? def.format(goal) : "—"}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 text-muted-foreground whitespace-nowrap">
                                {goalSum > 0 ? def.format(goalSum) : "—"}
                              </TableCell>
                              <TableCell className="border-l" />
                            </TableRow>
                          )}
                          {hasMonthlyGoals && (
                            <TableRow key={`${def.key}-status`} className="bg-muted/20">
                              <TableCell className="text-xs text-muted-foreground sticky left-0 bg-card z-10 border-r whitespace-nowrap pl-6">
                                Winning / Losing
                              </TableCell>
                              {createdMonthly.map((r, i) => {
                                const goal = monthlyGoals[i];
                                const actualVal = getMonthValue(r);
                                if (goal === undefined || goal <= 0 || actualVal === 0) {
                                  return (
                                    <TableCell
                                      key={months[i]}
                                      className="text-right tabular-nums text-xs text-muted-foreground whitespace-nowrap"
                                    >
                                      —
                                    </TableCell>
                                  );
                                }
                                const diffPct = ((actualVal - goal) / goal) * 100;
                                return (
                                  <TableCell
                                    key={months[i]}
                                    className={cn(
                                      "text-right tabular-nums text-xs font-semibold whitespace-nowrap",
                                      GOAL_TEXT[goalStatus(actualVal, goal)],
                                    )}
                                  >
                                    {diffPct >= 0 ? "+" : ""}
                                    {diffPct.toFixed(1)}%
                                  </TableCell>
                                );
                              })}
                              <TableCell colSpan={2} className="border-l" />
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// ── Non-Relational Goals Section ─────────────────────────────────────

function NonRelationalGoalsSection({
  data,
  monthlyTracker,
  onDrillDown,
  hideGpm = false,
}: {
  data: {
    targets: Record<string, number>;
    actuals: AnnualGoals;
    monthlyTracker: MonthlyRow[];
    sources: string[];
    nrChannelBreakdowns?: Record<
      string,
      {
        actuals: AnnualGoals;
        monthlyTracker: MonthlyRow[];
        targets: Record<string, number>;
      }
    >;
    sourceGroupDetails?: Record<string, string[]>;
  };
  monthlyTracker: MonthlyRow[];
  onDrillDown?: (req: DrillDownRequest) => void;
  hideGpm?: boolean;
}) {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  const { sources, nrChannelBreakdowns, sourceGroupDetails } = data;

  // Resolve active data: channel-specific or aggregate
  const channelData = selectedChannel && nrChannelBreakdowns?.[selectedChannel];
  const activeActuals = channelData ? channelData.actuals : data.actuals;
  const activeMonthly = channelData ? channelData.monthlyTracker : data.monthlyTracker;
  const activeTargets = channelData ? channelData.targets : data.targets;
  // For groups, pass constituent sources for drill-down filtering
  const activeSources = selectedChannel
    ? (sourceGroupDetails?.[selectedChannel] ?? [selectedChannel])
    : sources.flatMap((s) => sourceGroupDetails?.[s] ?? [s]);

  const months = monthlyTracker.map((r) => r.month);
  const trackerPrefix = selectedChannel ?? "NR";
  const trackerMetrics = buildTrackerMetrics(trackerPrefix, activeActuals, hideGpm, activeTargets);
  const { handleGridDrill, handleCellClick } = createSectionDrillHandlers(
    onDrillDown,
    // BYX is matched by campus name, not by source
    selectedChannel === "BYX" ? { section: "byx" } : { section: "nr", sources: activeSources },
    "NR",
  );

  const hasChannels =
    sources.length > 1 && nrChannelBreakdowns && Object.keys(nrChannelBreakdowns).length > 0;

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Non-Relational Goals
        </span>
      </div>

      {sources.length > 0 && !hasChannels ? (
        <p className="text-xs text-muted-foreground text-center -mt-2">
          Sources: {sources.join(", ")}
        </p>
      ) : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            <Target className="h-3.5 w-3.5" />
            Non-Relational Annual Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MetricTargetsGrid
            actuals={data.actuals}
            targets={data.targets}
            onDrillDown={handleGridDrill}
            hideGpm={hideGpm}
          />
        </CardContent>
      </Card>

      <MetricMonthlyTrackerTable
        title={
          selectedChannel ? `${selectedChannel} Monthly Tracker` : "Non-Relational Monthly Tracker"
        }
        metrics={trackerMetrics}
        monthlyData={activeMonthly}
        months={months}
        onCellClick={handleCellClick}
        headerContent={
          hasChannels ? (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <Badge
                variant={selectedChannel === null ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => setSelectedChannel(null)}
              >
                All
              </Badge>
              {sources.map((source) => {
                const groupSources = sourceGroupDetails?.[source];
                const badge = (
                  <Badge
                    key={source}
                    variant={selectedChannel === source ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setSelectedChannel(source === selectedChannel ? null : source)}
                  >
                    {source}
                  </Badge>
                );
                if (groupSources) {
                  return (
                    <TooltipProvider key={source} delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>{badge}</TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">{groupSources.join(", ")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return badge;
              })}
            </div>
          ) : undefined
        }
      />
    </>
  );
}

// ── Affiliate Goals Section ──────────────────────────────────────────

function AffiliateGoalsDashboardSection({
  data,
  monthlyTracker,
  onDrillDown,
  hideGpm = false,
}: {
  data: {
    targets: Record<string, number>;
    actuals: AnnualGoals;
    monthlyTracker: MonthlyRow[];
    exclusions: string[];
  };
  monthlyTracker: MonthlyRow[];
  onDrillDown?: (req: DrillDownRequest) => void;
  hideGpm?: boolean;
}) {
  const { targets, actuals, monthlyTracker: affMonthly, exclusions } = data;
  const months = monthlyTracker.map((r) => r.month);
  const trackerMetrics = buildTrackerMetrics("Aff", actuals, hideGpm, targets);
  const { handleGridDrill, handleCellClick } = createSectionDrillHandlers(
    onDrillDown,
    { section: "aff", affiliateExclusions: exclusions },
    "Aff",
  );

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Affiliate Goals
        </span>
      </div>

      {exclusions.length > 0 && (
        <p className="text-xs text-muted-foreground text-center -mt-2">
          Excluded: {exclusions.join(", ")}
        </p>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            <Target className="h-3.5 w-3.5" />
            Affiliate Annual Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MetricTargetsGrid
            actuals={actuals}
            targets={targets}
            onDrillDown={handleGridDrill}
            hideGpm={hideGpm}
          />
        </CardContent>
      </Card>

      <MetricMonthlyTrackerTable
        title="Affiliate Monthly Tracker"
        metrics={trackerMetrics}
        monthlyData={affMonthly}
        months={months}
        onCellClick={handleCellClick}
      />
    </>
  );
}

// ── Source Goal Set Section ──────────────────────────────────────────

function SourceGoalSetSection({
  set,
  monthlyTracker,
  onDrillDown,
  hideGpm = false,
}: {
  set: SourceGoalSetData;
  monthlyTracker: MonthlyRow[];
  onDrillDown?: (req: DrillDownRequest) => void;
  hideGpm?: boolean;
}) {
  const { name, sources, targets, actuals, monthlyTracker: setMonthly } = set;
  const months = monthlyTracker.map((r) => r.month);

  const metrics = buildMetricsList(actuals, hideGpm);
  const normalizedTargets = normalizeTargets(targets);
  const hasAnyTarget = metrics.some((m) => normalizedTargets[m.key] !== undefined);
  const trackerMetrics = buildTrackerMetrics(name, actuals, hideGpm, targets);
  const { handleGridDrill, handleCellClick } = createSectionDrillHandlers(
    onDrillDown,
    { section: "source", sources },
    name,
  );

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          {name}
        </span>
      </div>

      {hasAnyTarget && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              <Target className="h-3.5 w-3.5" />
              {name} Annual Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MetricTargetsGrid
              actuals={actuals}
              targets={targets}
              onDrillDown={handleGridDrill}
              hideGpm={hideGpm}
            />
          </CardContent>
        </Card>
      )}

      <MetricMonthlyTrackerTable
        title={`${name} Monthly Tracker`}
        metrics={trackerMetrics}
        monthlyData={setMonthly}
        months={months}
        onCellClick={handleCellClick}
      />
    </>
  );
}

// ── Campus Budget Section ────────────────────────────────────────────

function CampusBudgetSection({
  campusBudget,
  ownerId,
  monthlyTracker,
}: {
  campusBudget: { annualBudget: number; monthlySpend: Record<string, number> };
  ownerId: string;
  monthlyTracker: MonthlyRow[];
}) {
  const months = monthlyTracker.map((r) => r.month);
  const monthlyBudget = Math.round(campusBudget.annualBudget / 12);

  // Derive fiscal year from months
  const firstMonth = months[0];
  const fyStartYear = firstMonth ? Number(firstMonth.split("-")[0]) : new Date().getFullYear();
  const fiscalYear = fyStartYear + 1;

  const [spend, setSpend] = useState<Record<string, number>>({ ...campusBudget.monthlySpend });
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [savedMonth, setSavedMonth] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  // Reset when campusBudget changes (FY switch)
  useEffect(() => {
    setSpend({ ...campusBudget.monthlySpend });
    setEditValues({});
  }, [campusBudget]);

  const totalSpent = Object.values(spend).reduce((sum, v) => sum + v, 0);
  const totalRemaining = campusBudget.annualBudget - totalSpent;

  async function saveSpend(month: string, rawValue: string) {
    const amount = rawValue === "" ? null : Number(rawValue);
    if (Number.isNaN(amount)) return;

    // Skip if unchanged
    const currentAmount = spend[month] ?? 0;
    if ((amount ?? 0) === currentAmount) return;

    const res = await fetch(`/api/owners/${ownerId}/campus-budget-spend`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, month, amount }),
    });

    if (res.ok) {
      const data = await res.json();
      // Rebuild spend from response
      const newSpend: Record<string, number> = {};
      for (const s of data.spend as { month: string; amount: number }[]) {
        newSpend[s.month] = s.amount;
      }
      setSpend(newSpend);
      clearTimeout(savedTimerRef.current);
      setSavedMonth(month);
      savedTimerRef.current = setTimeout(() => setSavedMonth(null), 800);
    }
  }

  function getEditValue(month: string): string {
    if (editValues[month] !== undefined) return editValues[month];
    const val = spend[month];
    return val != null && val !== 0 ? String(val) : "";
  }

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Campus Budget
        </span>
      </div>

      {/* Budget Summary */}
      <Card className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/[0.03] to-transparent pointer-events-none" />
        <CardContent className="flex items-center gap-6 py-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Campus Budget
            </p>
            <p className="text-2xl font-bold tabular-nums tracking-tight">
              {formatCurrency(campusBudget.annualBudget)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(monthlyBudget)}/mo allocation
            </p>
          </div>
          <Separator orientation="vertical" className="h-12" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Travel, Meals, Systems, prizes, ad spend, campus activations, etc.
          </p>
        </CardContent>
      </Card>

      {/* Budget vs Spend Table (full width) */}
      <Card>
        <CardContent className="px-0 py-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs uppercase tracking-wider sticky left-0 bg-card z-10 border-r min-w-[120px]">
                    Row
                  </TableHead>
                  {months.map((m) => (
                    <TableHead
                      key={m}
                      className="text-right text-xs uppercase tracking-wider whitespace-nowrap"
                    >
                      {formatMonthLabel(m)}
                    </TableHead>
                  ))}
                  <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l-2 whitespace-nowrap">
                    Total
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Budget row */}
                <TableRow className="hover:bg-muted/30">
                  <TableCell className="text-xs font-medium sticky left-0 bg-card z-10 border-r whitespace-nowrap">
                    Budget
                  </TableCell>
                  {months.map((m) => (
                    <TableCell
                      key={m}
                      className="text-right tabular-nums text-xs whitespace-nowrap"
                    >
                      {formatCurrency(monthlyBudget)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
                    {formatCurrency(campusBudget.annualBudget)}
                  </TableCell>
                </TableRow>
                {/* Spent row (editable) */}
                <TableRow className="hover:bg-muted/30">
                  <TableCell className="text-xs font-medium sticky left-0 bg-card z-10 border-r whitespace-nowrap">
                    Spent
                  </TableCell>
                  {months.map((m) => (
                    <TableCell key={m} className="text-right p-1">
                      <CurrencyInput
                        id={`spend-${m}`}
                        value={getEditValue(m)}
                        onChange={(v) => setEditValues((prev) => ({ ...prev, [m]: v }))}
                        onBlur={() => {
                          const val = getEditValue(m);
                          saveSpend(m, val);
                          setEditValues((prev) => {
                            const next = { ...prev };
                            delete next[m];
                            return next;
                          });
                        }}
                        className={cn(
                          "h-7 w-20 text-right tabular-nums text-xs px-1.5 ml-auto",
                          savedMonth === m &&
                            "border-green-500 ring-[3px] ring-green-500/20 transition-[border-color,box-shadow] duration-300",
                        )}
                        placeholder="—"
                      />
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
                    {formatCurrency(totalSpent)}
                  </TableCell>
                </TableRow>
                {/* Remaining row */}
                <TableRow className="bg-muted/20">
                  <TableCell className="text-xs font-semibold sticky left-0 bg-card z-10 border-r whitespace-nowrap">
                    Remaining
                  </TableCell>
                  {months.map((m) => {
                    const monthSpend = spend[m] ?? 0;
                    const remaining = monthlyBudget - monthSpend;
                    return (
                      <TableCell
                        key={m}
                        className={cn(
                          "text-right tabular-nums text-xs whitespace-nowrap",
                          remaining < 0 && "text-destructive",
                        )}
                      >
                        {formatCurrency(remaining)}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    className={cn(
                      "text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap",
                      totalRemaining < 0 && "text-destructive",
                    )}
                  >
                    {formatCurrency(totalRemaining)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// ── BYX Section ──────────────────────────────────────────────────────

function ByxSection({
  data,
  monthlyTracker,
  onDrillDown,
}: {
  data: { actuals: AnnualGoals; monthlyTracker: MonthlyRow[] };
  monthlyTracker: MonthlyRow[];
  onDrillDown?: (req: DrillDownRequest) => void;
}) {
  const { actuals } = data;
  const months = monthlyTracker.map((r) => r.month);
  const { handleGridDrill, handleCellClick } = createSectionDrillHandlers(
    onDrillDown,
    { section: "byx" },
    "BYX",
  );

  const stats = [
    { label: "Opportunities", metric: "opportunities" as const, value: actuals.opportunities },
    { label: "Deals Closed", metric: "closedDeals" as const, value: actuals.closedDeals },
  ];

  const trackerMetrics: TrackerMetric[] = [
    {
      label: "BYX Opportunities",
      drillMetric: "opportunities",
      getValue: (r) => String(r.opportunities),
      getTotal: () => formatNumber(actuals.opportunities),
    },
    {
      label: "BYX Deals Closed",
      drillMetric: "closedDeals",
      getValue: (r) => String(r.closedDeals),
      getTotal: () => formatNumber(actuals.closedDeals),
    },
  ];

  return (
    <>
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          BYX
        </span>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            <GraduationCap className="h-3.5 w-3.5" />
            BYX This Fiscal Year
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            {stats.map((s) => (
              <div key={s.metric}>
                <dt className="text-xs text-muted-foreground">BYX {s.label}</dt>
                <dd>
                  <DrillableValue
                    disabled={!onDrillDown}
                    onClick={() => handleGridDrill(s.metric, s.label)}
                  >
                    <span className="text-lg font-bold tabular-nums">{formatNumber(s.value)}</span>
                  </DrillableValue>
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <MetricMonthlyTrackerTable
        title="BYX Monthly Tracker"
        metrics={trackerMetrics}
        monthlyData={data.monthlyTracker}
        months={months}
        onCellClick={handleCellClick}
      />
    </>
  );
}

// ── Campus KPIs Section ──────────────────────────────────────────────

interface CampusKPIsData {
  campuses: { campus: string; year: number | null; startDate: string | null }[];
  monthlyData: { campus: string; month: string; leads: number; deals: number }[];
  commissionRates: { year2: number | null; year3: number | null };
}

function CampusKPIsSection({
  data,
  monthlyTracker,
  campusTargets,
  onDrillDown,
}: {
  data: CampusKPIsData;
  monthlyTracker: MonthlyRow[];
  campusTargets: { leads: number | null; dealsWon: number | null };
  onDrillDown?: (req: DrillDownRequest) => void;
}) {
  const { campuses, monthlyData, commissionRates } = data;

  // Build month columns from monthlyTracker (already in fiscal order)
  const months = monthlyTracker.map((r) => r.month);

  // Group campuses by commission tier: Year 2 → year2 rate, Year 3 → year3 rate
  const year2Campuses = campuses
    .filter((c) => c.year !== null && c.year === 2)
    .sort((a, b) => a.campus.localeCompare(b.campus));
  const year3Campuses = campuses
    .filter((c) => c.year !== null && c.year === 3)
    .sort((a, b) => a.campus.localeCompare(b.campus));
  const unconfiguredCampuses = campuses
    .filter((c) => c.year === null)
    .sort((a, b) => a.campus.localeCompare(b.campus));

  // Build lookup: campus|month → deals count
  const dealLookup = new Map<string, number>();
  const leadLookup = new Map<string, number>();
  for (const d of monthlyData) {
    dealLookup.set(`${d.campus}|${d.month}`, d.deals);
    leadLookup.set(`${d.campus}|${d.month}`, d.leads);
  }

  function getDeals(campus: string, month: string): number {
    return dealLookup.get(`${campus}|${month}`) ?? 0;
  }

  function getLeads(campus: string, month: string): number {
    return leadLookup.get(`${campus}|${month}`) ?? 0;
  }

  // Compute totals
  const allCampusNames = campuses.map((c) => c.campus);
  const totalLeads = allCampusNames.reduce(
    (sum, c) => sum + months.reduce((s, m) => s + getLeads(c, m), 0),
    0,
  );
  const totalDeals = allCampusNames.reduce(
    (sum, c) => sum + months.reduce((s, m) => s + getDeals(c, m), 0),
    0,
  );
  const conversionRate = totalLeads > 0 ? totalDeals / totalLeads : 0;

  // Group totals per year
  function yearDealsTotal(yearCampuses: { campus: string }[]): number {
    return yearCampuses.reduce(
      (sum, c) => sum + months.reduce((s, m) => s + getDeals(c.campus, m), 0),
      0,
    );
  }

  const y2Total = yearDealsTotal(year2Campuses);
  const y3Total = yearDealsTotal(year3Campuses);

  const y2Payout = commissionRates.year2 ? y2Total * commissionRates.year2 : 0;
  const y3Payout = commissionRates.year3 ? y3Total * commissionRates.year3 : 0;

  // Monthly totals per year group
  function yearMonthlyDeals(yearCampuses: { campus: string }[], month: string): number {
    return yearCampuses.reduce((sum, c) => sum + getDeals(c.campus, month), 0);
  }

  function allMonthlyDeals(month: string): number {
    return allCampusNames.reduce((sum, c) => sum + getDeals(c, month), 0);
  }

  const y2Rate = commissionRates.year2 ?? 0;
  const y3Rate = commissionRates.year3 ?? 0;

  return (
    <>
      {/* Divider with label */}
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Campus KPIs
        </span>
      </div>

      {/* Top row — Annual Campus Goals + Campus Commission Structure side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Annual Campus Goals */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/[0.03] to-transparent pointer-events-none" />
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              <Target className="h-3.5 w-3.5" />
              Annual Campus Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-muted-foreground">Campus ER Leads Generated</dt>
                <dd className="flex items-baseline gap-2">
                  <DrillableValue
                    disabled={!onDrillDown}
                    onClick={() =>
                      onDrillDown?.({
                        metric: "opportunities",
                        section: "campus",
                        label: "Campus ER Leads",
                      })
                    }
                  >
                    <span className="text-lg font-bold tabular-nums">
                      {formatNumber(totalLeads)}
                    </span>
                  </DrillableValue>
                  {campusTargets.leads !== null && (
                    <span className="text-xs text-muted-foreground">
                      / {formatNumber(campusTargets.leads)} target
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Campus ER Deals Won</dt>
                <dd className="flex items-baseline gap-2">
                  <DrillableValue
                    disabled={!onDrillDown}
                    onClick={() =>
                      onDrillDown?.({
                        metric: "closedDeals",
                        section: "campus",
                        label: "Campus ER Deals Won",
                      })
                    }
                  >
                    <span className="text-lg font-bold tabular-nums">
                      {formatNumber(totalDeals)}
                    </span>
                  </DrillableValue>
                  {campusTargets.dealsWon !== null && (
                    <span className="text-xs text-muted-foreground">
                      / {formatNumber(campusTargets.dealsWon)} target
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Conversion Rate</dt>
                <dd className="flex items-baseline gap-2">
                  <span className="text-lg font-bold tabular-nums">
                    {totalLeads > 0 ? formatPercent(conversionRate) : "—"}
                  </span>
                  {campusTargets.leads !== null &&
                    campusTargets.dealsWon !== null &&
                    campusTargets.leads > 0 && (
                      <span className="text-xs text-muted-foreground">
                        / {formatPercent(campusTargets.dealsWon / campusTargets.leads)} target
                      </span>
                    )}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Campus Commission Structure */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.04] to-transparent pointer-events-none" />
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              <GraduationCap className="h-3.5 w-3.5" />
              Campus Commission Structure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed text-muted-foreground">
              For every engagement ring sold on the campuses you oversee: Year 2 campuses earn{" "}
              {commissionRates.year2 ? (
                <span className="font-semibold text-foreground">
                  {formatCurrency(commissionRates.year2)}
                </span>
              ) : (
                <span className="italic">—</span>
              )}{" "}
              and Year 3 campuses earn{" "}
              {commissionRates.year3 ? (
                <span className="font-semibold text-foreground">
                  {formatCurrency(commissionRates.year3)}
                </span>
              ) : (
                <span className="italic">—</span>
              )}
              .
            </p>

            <Separator className="my-3" />

            <dl className="space-y-2">
              <div className="flex items-center justify-between">
                <dt className="text-xs text-muted-foreground">Year 2 Payout</dt>
                <dd className="text-sm font-bold tabular-nums">{formatCurrency(y2Payout)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-xs text-muted-foreground">Year 3 Payout</dt>
                <dd className="text-sm font-bold tabular-nums">{formatCurrency(y3Payout)}</dd>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <dt className="text-xs font-medium">Total Campus Payout</dt>
                <dd className="text-base font-bold tabular-nums text-accent-foreground">
                  {formatCurrency(y2Payout + y3Payout)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Full-width KPI Progress Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            KPI Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <TooltipProvider>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs uppercase tracking-wider sticky left-0 bg-card z-10 border-r min-w-[180px]">
                      Monthly Tracker
                    </TableHead>
                    {months.map((m) => (
                      <TableHead
                        key={m}
                        className="text-right text-xs uppercase tracking-wider whitespace-nowrap"
                      >
                        {formatMonthLabel(m)}
                      </TableHead>
                    ))}
                    <TableHead className="text-right text-xs uppercase tracking-wider font-bold border-l-2">
                      Total
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Year 2 campuses */}
                  {year2Campuses.map((c) => (
                    <CampusRow
                      key={c.campus}
                      campus={c.campus}
                      year={c.year}
                      startDate={c.startDate}
                      months={months}
                      getDeals={getDeals}
                      onDrillDown={onDrillDown}
                    />
                  ))}
                  {year2Campuses.length > 0 && (
                    <>
                      <SummaryRow
                        label="Monthly Year 2 School Total"
                        months={months}
                        getMonthValue={(m) => yearMonthlyDeals(year2Campuses, m)}
                        total={y2Total}
                      />
                      <SummaryRow
                        label="Monthly Year 2 School Payout"
                        months={months}
                        getMonthValue={(m) => yearMonthlyDeals(year2Campuses, m) * y2Rate}
                        total={y2Payout}
                        isCurrency
                        className="border-b-2"
                      />
                    </>
                  )}

                  {/* Year 3 campuses */}
                  {year3Campuses.map((c) => (
                    <CampusRow
                      key={c.campus}
                      campus={c.campus}
                      year={c.year}
                      startDate={c.startDate}
                      months={months}
                      getDeals={getDeals}
                      onDrillDown={onDrillDown}
                    />
                  ))}
                  {year3Campuses.length > 0 && (
                    <>
                      <SummaryRow
                        label="Monthly Year 3 School Total"
                        months={months}
                        getMonthValue={(m) => yearMonthlyDeals(year3Campuses, m)}
                        total={y3Total}
                      />
                      <SummaryRow
                        label="Monthly Year 3 School Payout"
                        months={months}
                        getMonthValue={(m) => yearMonthlyDeals(year3Campuses, m) * y3Rate}
                        total={y3Payout}
                        isCurrency
                        className="border-b-2"
                      />
                    </>
                  )}

                  {/* Unconfigured campuses (no start date) */}
                  {unconfiguredCampuses.map((c) => (
                    <CampusRow
                      key={c.campus}
                      campus={c.campus}
                      year={c.year}
                      startDate={c.startDate}
                      months={months}
                      getDeals={getDeals}
                      onDrillDown={onDrillDown}
                    />
                  ))}

                  {/* Grand totals */}
                  <SummaryRow
                    label="Total Monthly Deals Won"
                    months={months}
                    getMonthValue={allMonthlyDeals}
                    total={totalDeals}
                    isGrandTotal
                  />
                  <SummaryRow
                    label="Total Monthly Payout"
                    months={months}
                    getMonthValue={(m) =>
                      yearMonthlyDeals(year2Campuses, m) * y2Rate +
                      yearMonthlyDeals(year3Campuses, m) * y3Rate
                    }
                    total={y2Payout + y3Payout}
                    isCurrency
                    isGrandTotal
                  />
                </TableBody>
              </Table>
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>
    </>
  );
}

function CampusRow({
  campus,
  year,
  startDate,
  months,
  getDeals,
  onDrillDown,
}: {
  campus: string;
  year: number | null;
  startDate: string | null;
  months: string[];
  getDeals: (campus: string, month: string) => number;
  onDrillDown?: (req: DrillDownRequest) => void;
}) {
  const total = months.reduce((sum, m) => sum + getDeals(campus, m), 0);
  const yearLabel = year !== null ? `Year ${year}` : "Year ?";
  const tooltipText = startDate
    ? `Started: ${formatStartDate(startDate)}`
    : "No start date configured";
  return (
    <TableRow className="hover:bg-muted/30">
      <TableCell className="text-xs font-medium sticky left-0 bg-card z-10 border-r whitespace-nowrap">
        {campus}{" "}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "font-normal cursor-help",
                year === null ? "text-amber-500" : "text-muted-foreground",
              )}
            >
              ({yearLabel})
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{tooltipText}</TooltipContent>
        </Tooltip>
      </TableCell>
      {months.map((m) => {
        const val = getDeals(campus, m);
        return (
          <TableCell
            key={m}
            className={cn(
              "text-right tabular-nums text-xs whitespace-nowrap",
              val > 0 && "text-foreground",
              val === 0 && "text-muted-foreground/40",
            )}
          >
            <DrillableValue
              disabled={!onDrillDown || val === 0}
              onClick={() =>
                onDrillDown?.({
                  metric: "closedDeals",
                  section: "campus",
                  campus,
                  month: m,
                  label: `${campus} — ${formatMonthLabel(m)}`,
                })
              }
            >
              {val}
            </DrillableValue>
          </TableCell>
        );
      })}
      <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
        <DrillableValue
          disabled={!onDrillDown || total === 0}
          onClick={() =>
            onDrillDown?.({
              metric: "closedDeals",
              section: "campus",
              campus,
              label: `${campus} — Annual`,
            })
          }
        >
          {total}
        </DrillableValue>
      </TableCell>
    </TableRow>
  );
}

function SummaryRow({
  label,
  months,
  getMonthValue,
  total,
  isCurrency = false,
  isGrandTotal = false,
  className: extraClass,
}: {
  label: string;
  months: string[];
  getMonthValue: (month: string) => number;
  total: number;
  isCurrency?: boolean;
  isGrandTotal?: boolean;
  className?: string;
}) {
  const fmt = isCurrency ? formatCurrency : formatNumber;
  return (
    <TableRow
      className={cn(isGrandTotal ? "bg-muted/40 font-semibold" : "bg-muted/20", extraClass)}
    >
      <TableCell
        className={cn(
          "text-xs font-semibold sticky left-0 bg-card z-10 border-r whitespace-nowrap",
          isGrandTotal ? "bg-muted/40" : "bg-muted/20",
        )}
      >
        {label}
      </TableCell>
      {months.map((m) => {
        const val = getMonthValue(m);
        return (
          <TableCell key={m} className="text-right tabular-nums text-xs whitespace-nowrap">
            {val === 0 && !isCurrency ? "—" : fmt(val)}
          </TableCell>
        );
      })}
      <TableCell className="text-right tabular-nums text-xs font-bold border-l-2 whitespace-nowrap">
        {fmt(total)}
      </TableCell>
    </TableRow>
  );
}
